// Advanced Calculator - Mobile Wizard Interface
// FULL implementation with all 50+ fields

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SimulationEngine } from '../SimulationEngine';
import { FinancialConfig } from '../FinancialConfig';
import { PDFExportManager } from '../PDFExportManager';
import { historicalData } from '../data';
import {
    computeFloorCeilingSeries,
    computeVanguardGuardrailsSeries,
    computeDynamicSWRSeries,
    computeFixedSustainableBinarySearch
} from '../AdvancedCalculatorShared';
import {
    buildHistoricalReturnsSnapshot,
    resolveBaselineReturns,
    applySequenceRiskToReturns,
    computeStrategyMSI,
    computeStrategyFullResult,
    buildBaselineCalcInputs,
    buildWhatIfCalcInputs,
    computeRollingPeriodAnalysis,
} from '../whatIfEngine.js';
import { runMonteCarloSimulationAsync } from '../MonteCarloEngine';
import MobileLoadingSpinner from './MobileLoadingSpinner';
import FixedIncomeVariesNote, { buildFixedIncomeVariesNote } from './FixedIncomeVariesNote.js';
import { EmailCaptureForm } from './EmailCaptureForm';
import { FreeRunsEmailGate } from './FreeRunsEmailGate.js';
import { SubscriptionManager } from '../SubscriptionManager';
import { openPricingModal } from '../utils/openPricingModal.js';
import {
  alertCheckoutFailure,
  startPdfUnlockCheckout,
} from '../utils/recommendedCheckout.js';
import {
  trackAdvancedRunClicked,
  trackAdvancedRunSucceeded,
} from '../utils/funnelAnalytics.js';
import { AreaChart, Area, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import CalculatorBridge from '../CalculatorBridge';
import { defaultLumpSumWithdrawalRows, MAX_LUMP_SUM_WITHDRAWALS, normalizeLumpSumWithdrawalRows, getVisibleLumpSumRows, coerceLumpSumRowsForProjection, parseLumpSumWithdrawals, getLumpSumScheduleDiagnostics } from '../lumpSumWithdrawals.js';
import { defaultLumpSumContributionRows, normalizeLumpSumContributionRows, getVisibleLumpSumContributionRows, coerceLumpSumContributionRowsForProjection, parseLumpSumContributions, getLumpSumContributionScheduleDiagnostics } from '../lumpSumContributions.js';
import CalculationTransparency from './CalculationTransparency.js';

export const AdvancedCalculatorMobile = ({ onBack }) => {
    const [expandedSection, setExpandedSection] = useState(0);
    const [hasCalculated, setHasCalculated] = useState(false);
    const [freeRunsStatus, setFreeRunsStatus] = useState(() => SubscriptionManager.canRunAdvancedFree());
    const [showFreeRunsEmailGate, setShowFreeRunsEmailGate] = useState(
        () => !SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR)
            && SubscriptionManager.canRunAdvancedFree().reason === 'needs_email'
    );
    const hasAdvancedAccess = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.ADVANCED_CALCULATOR);
    
    // Load saved inputs from localStorage
    const getInitialInputs = () => {
        try {
            const saved = localStorage.getItem('advancedCalculatorMobile_inputs');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (!Array.isArray(parsed.lumpSumWithdrawals)) {
                    parsed.lumpSumWithdrawals = defaultLumpSumWithdrawalRows();
                } else {
                    parsed.lumpSumWithdrawals = normalizeLumpSumWithdrawalRows(parsed.lumpSumWithdrawals);
                }
                if (!Array.isArray(parsed.lumpSumContributions)) {
                    parsed.lumpSumContributions = defaultLumpSumContributionRows();
                } else {
                    parsed.lumpSumContributions = normalizeLumpSumContributionRows(parsed.lumpSumContributions);
                }
                const allocSum =
                    (Number(parsed.sp500) || 0) +
                    (Number(parsed.ausShares) || 0) +
                    (Number(parsed.intlShares) || 0) +
                    (Number(parsed.bonds) || 0) +
                    (Number(parsed.cash) || 0);
                if (Math.abs(allocSum) < 0.5) {
                    parsed.sp500 = 35;
                    parsed.ausShares = 15;
                    parsed.intlShares = 20;
                    parsed.bonds = 20;
                    parsed.cash = 10;
                }
                return parsed;
            }
        } catch (error) {
            console.error('Error loading saved inputs:', error);
        }
        
        // FULL ADVANCED FIELDS - EXACT DESKTOP DEFAULTS (AdvancedState.js)
        return {
            relationshipStatus: 'single',
            yourAge: 65,
            yourGender: 'male',
            yourSuper: 500000,
            yourTbcUsed: 0,
            partnerAge: 65,
            partnerGender: 'female',
            partnerSuper: '',
            partnerTbcUsed: '',
            // Retirement Plan
            periodLength: 30,
            targetFinalBalance: 100000,
            // Other Income & Assets
            otherTaxableIncome: '',
            otherNonTaxableIncome: '',
            otherAssets: '',
            isHomeowner: true,
            includePension: true,
            pensionGrowthRate: 0.3,
            // Health Insurance
            hasPrivateHealthInsurance: true,
            partnerHasPrivateHealthInsurance: true,
            // Annuities & DB Pensions
            annuityPurchasePrice: '',
            annuityAnnualPayment: '',
            annuityIsIndexed: true,
            definedBenefitPensionAmount: '',
            definedBenefitIsIndexed: true,
            // Portfolio Allocation — balanced 100% so the first free run succeeds
            sp500: 35,
            ausShares: 15,
            intlShares: 20,
            bonds: 20,
            cash: 10,
            // Alternatives / Infrastructure (from SMSF)
            infrastructurePct: 0,
            infrastructureValue: '',
            frankingCredits: 80,
            // Withdrawal Strategy
            withdrawalStrategy: 'fixed',
            monteCarloTargetIncome: '',
            lumpSumWithdrawals: defaultLumpSumWithdrawalRows(),
            lumpSumContributions: defaultLumpSumContributionRows(),
            // Strategy-specific parameters (not in desktop state, but needed)
            dynInitPct: 4,
            vgInitPct: 4,
            fcInitPct: 4,
            fcFloorPct: 3,
            fcCeilPct: 6,
            // Phased Retirement
            phasedRetirement: false,
            yourRetirementAge: 67,
            partnerRetirementAge: 67,
            yourSalary: '',
            yourSuperContributions: '',
            partnerSalary: '',
            partnerSuperContributions: '',
            // either | primary — Fixed MSI lifestyle drawdown trigger for couples
            msiDrawdownTrigger: 'either',
            // Bridge / delayed super access
            enableBridgeStrategy: false,
            yourSuperAccessAge: 67,
            partnerSuperAccessAge: 67,
            bridgeCashBucket: '',
            bridgeInvestmentBucket: '',
            bridgeInvestmentReturnRate: 3,
            enableMortgageComparison: false,
            mortgageBalance: '',
            mortgageInterestRate: 6,
            mortgageAnnualRepayment: '',
            mortgageStrategy: 'keep',
            mortgagePayoffSource: 'super',
            mortgagePayoffScheduleType: 'year',
            mortgagePayoffScheduleValue: '',
            mortgagePartialPayoffAmount: '',
            // Start Year - Match desktop default
            startYear: 1980,
            // Fees - Match desktop defaults
            adminFees: 52,
            adminFeePercentage: 0.10,
            investmentFees: 0.5,
            investmentFeeCap: 350,
            transactionCosts: 0.08,
            insuranceFees: '',
            // TBC - Match desktop (FinancialConfig.TRANSFER_BALANCE_CAP)
            generalTransferBalanceCap: FinancialConfig.TRANSFER_BALANCE_CAP,
            // Alternative Assets (from SMSF transfers)
            transferFromSMSF: false,
            residentialPropertyValue: '',
            residentialRentalIncome: '',
            residentialPropertyRentalYield: '',
            residentialPropertyCapitalGrowth: '',
            commercialPropertyValue: '',
            commercialRentalIncome: '',
            commercialPropertyRentalYield: '',
            commercialPropertyCapitalGrowth: '',
            collectiblesValue: '',
            collectiblesCapitalGrowth: '',
            otherAlternativesValue: '',
            otherAlternativesCapitalGrowth: ''
        };
    };
    
    const [inputs, setInputs] = useState(getInitialInputs());
    const [results, setResults] = useState(null);
    const [rollingMSI, setRollingMSI] = useState([]);
    const [isScrolled, setIsScrolled] = useState(false);
    const [showDetailedChart, setShowDetailedChart] = useState(false);
    const [showWhatIf, setShowWhatIf] = useState(true);
    const [activeTab, setActiveTab] = useState('historical'); // 'historical' or 'monteCarlo'
    const [monteCarloResults, setMonteCarloResults] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const [fixedIncomeVariesNote, setFixedIncomeVariesNote] = useState(null);
    const [isMonteCarloRunning, setIsMonteCarloRunning] = useState(false);
    const monteCarloRunIdRef = useRef(0);
    const [calculationMessage, setCalculationMessage] = useState('');
    const [isExporting, setIsExporting] = useState(false);
    // EXACT desktop what-if state (3 scenarios)
    const [whatIfPeriod, setWhatIfPeriod] = useState(0);
    const [whatIfPeriodResult, setWhatIfPeriodResult] = useState(null);
    const [whatIfFeeChangePct, setWhatIfFeeChangePct] = useState('');
    const [whatIfFeeChangeResult, setWhatIfFeeChangeResult] = useState(null);
    const [whatIfSeqRiskPct, setWhatIfSeqRiskPct] = useState('');
    const [whatIfSeqRiskResult, setWhatIfSeqRiskResult] = useState(null);
    const lastRunInputsRef = useRef(null);
    const lastReturnsByYearRef = useRef(null);
    const lastRunStrategyRef = useRef('fixed');
    const currentStrategyMSIRef = useRef(0);
    const resultsRef = useRef(null);
    const lumpSumWithdrawalsRef = useRef(inputs.lumpSumWithdrawals);
    const lumpSumContributionsRef = useRef(inputs.lumpSumContributions);
    
    // Effect to enforce targetFinalBalance = 0 for Floor & Ceiling strategy
    useEffect(() => {
        if (inputs.withdrawalStrategy === 'FloorAndCeiling' && inputs.targetFinalBalance !== 0) {
            setInputs(prev => ({ ...prev, targetFinalBalance: 0 }));
        }
    }, [inputs.withdrawalStrategy, inputs.targetFinalBalance]);
    
    // Auto-switch to Fixed strategy if a dynamic strategy is selected and either partner is below preservation age
    useEffect(() => {
        const dynamicStrategies = ['dynamicSWR', 'vanguardGuardrails', 'floorCeiling'];
        const isDynamicStrategy = dynamicStrategies.includes(inputs.withdrawalStrategy);
        
        if (isDynamicStrategy) {
            const isCouple = inputs.relationshipStatus === 'couple';
            const yourAgeNum = Number(inputs.yourAge) || 0;
            const partnerAgeNum = Number(inputs.partnerAge) || 0;
            const preservationAge = 60;
            const youBelowPreservation = yourAgeNum < preservationAge;
            const partnerBelowPreservation = isCouple && partnerAgeNum < preservationAge;
            const eitherBelowPreservation = youBelowPreservation || partnerBelowPreservation;
            
            if (eitherBelowPreservation) {
                // Switch to Fixed strategy automatically
                setInputs(prev => ({ ...prev, withdrawalStrategy: 'fixed' }));
            }
        }
    }, [inputs.withdrawalStrategy, inputs.yourAge, inputs.partnerAge, inputs.relationshipStatus]);

    // Load transfer data from CalculatorBridge (e.g., SMSF → Advanced handoff)
    useEffect(() => {
        try {
            const transferData = CalculatorBridge.retrieveTransferData('advanced');
            if (transferData) {
                const merged = {
                    ...inputs,
                    // Personal and fees
                    relationshipStatus: transferData.relationshipStatus ?? inputs.relationshipStatus,
                    yourAge: transferData.yourAge ?? inputs.yourAge,
                    yourSuper: transferData.yourSuper ?? inputs.yourSuper,
                    partnerAge: transferData.partnerAge ?? inputs.partnerAge,
                    partnerSuper: transferData.partnerSuper ?? inputs.partnerSuper,
                    adminFees: transferData.adminFees ?? inputs.adminFees,
                    adminFeePercentage: transferData.adminFeePercentage ?? inputs.adminFeePercentage,
                    investmentFees: transferData.investmentFees ?? inputs.investmentFees,
                    insuranceFees: transferData.insuranceFees ?? inputs.insuranceFees,
                    isHomeowner: transferData.isHomeowner ?? inputs.isHomeowner,
                    includePension: transferData.includePension ?? inputs.includePension,
                    startYear: transferData.startYear ?? inputs.startYear,
                    periodLength: transferData.periodLength ?? inputs.periodLength,
                    // Portfolio
                    sp500: transferData.sp500 ?? inputs.sp500,
                    ausShares: transferData.ausShares ?? inputs.ausShares,
                    intlShares: transferData.intlShares ?? inputs.intlShares,
                    bonds: transferData.bonds ?? inputs.bonds,
                    cash: transferData.cash ?? inputs.cash,
                    frankingCredits: transferData.frankingCredits ?? inputs.frankingCredits,
                    // Alternatives/Infrastructure
                    infrastructurePct: transferData.infrastructurePct ?? inputs.infrastructurePct,
                    infrastructureValue: transferData.infrastructureValue ?? inputs.infrastructureValue,
                    // Alternative assets (property/collectibles)
                    transferFromSMSF: transferData.transferFromSMSF ?? inputs.transferFromSMSF,
                    residentialPropertyValue: transferData.residentialPropertyValue ?? inputs.residentialPropertyValue,
                    residentialRentalIncome: transferData.residentialRentalIncome ?? inputs.residentialRentalIncome,
                    residentialPropertyRentalYield: transferData.residentialPropertyRentalYield ?? inputs.residentialPropertyRentalYield,
                    residentialPropertyCapitalGrowth: transferData.residentialPropertyCapitalGrowth ?? inputs.residentialPropertyCapitalGrowth,
                    commercialPropertyValue: transferData.commercialPropertyValue ?? inputs.commercialPropertyValue,
                    commercialRentalIncome: transferData.commercialRentalIncome ?? inputs.commercialRentalIncome,
                    commercialPropertyRentalYield: transferData.commercialPropertyRentalYield ?? inputs.commercialPropertyRentalYield,
                    commercialPropertyCapitalGrowth: transferData.commercialPropertyCapitalGrowth ?? inputs.commercialPropertyCapitalGrowth,
                    collectiblesValue: transferData.collectiblesValue ?? inputs.collectiblesValue,
                    collectiblesCapitalGrowth: transferData.collectiblesCapitalGrowth ?? inputs.collectiblesCapitalGrowth
                };
                setInputs(merged);
                localStorage.setItem('advancedCalculatorMobile_inputs', JSON.stringify(merged));
                // Clear after applying to avoid reapplying on subsequent mounts
                CalculatorBridge.clearTransferData('advanced');
            }
        } catch (_e) {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply budget-tool target income handoff from /app.html#advanced?target_income=...
    useEffect(() => {
        try {
            const searchParams = new URLSearchParams(window.location.search);
            const hash = window.location.hash || '';
            const hashParams = hash.includes('?') ? new URLSearchParams(hash.split('?')[1]) : new URLSearchParams();
            const targetIncomeParam = searchParams.get('target_income') || hashParams.get('target_income');
            const targetIncome = Math.max(0, Math.round(Number(targetIncomeParam) || 0));
            if (targetIncome > 0) {
                setInputs(prev => ({
                    ...prev,
                    monteCarloTargetIncome: targetIncome
                }));
            }
        } catch (_e) {
            // Ignore malformed handoff URLs.
        }
    }, []);
    
    // Desktop's calculateWhatIfScenario wrapper
    const calculateWhatIfScenario = async (scenarioName, calculationFn) => {
        try {
            const result = await calculationFn();
            return { success: true, data: result };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };
    
    // Desktop's validation helper
    const validateInput = (value, min = -Infinity, max = Infinity, defaultValue = 0) => {
        const num = Number(value);
        if (isNaN(num) || num < min || num > max) {
            return defaultValue;
        }
        return num;
    };
    
    // Save inputs to localStorage
    React.useEffect(() => {
        try {
            localStorage.setItem('advancedCalculatorMobile_inputs', JSON.stringify(inputs));
        } catch (error) {
            console.error('Error saving inputs:', error);
        }
    }, [inputs]);
    
    // Hide header on scroll
    React.useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.pageYOffset > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    
    const updateLumpSumRow = useCallback((idx, patch) => {
        setInputs((prev) => {
            const nextLumps = normalizeLumpSumWithdrawalRows(prev.lumpSumWithdrawals).map((row, i) =>
                i === idx ? { ...row, ...patch } : row
            );
            lumpSumWithdrawalsRef.current = nextLumps;
            return {
                ...prev,
                lumpSumWithdrawals: nextLumps,
            };
        });
    }, []);

    const clearAllLumpSumWithdrawals = useCallback(() => {
        const emptyRows = defaultLumpSumWithdrawalRows();
        lumpSumWithdrawalsRef.current = emptyRows;
        setInputs((prev) => ({
            ...prev,
            lumpSumWithdrawals: emptyRows,
        }));
    }, []);

    const updateLumpSumContributionRow = useCallback((idx, patch) => {
        setInputs((prev) => {
            const nextContributions = normalizeLumpSumContributionRows(prev.lumpSumContributions).map((row, i) =>
                i === idx ? { ...row, ...patch } : row
            );
            lumpSumContributionsRef.current = nextContributions;
            return {
                ...prev,
                lumpSumContributions: nextContributions,
            };
        });
    }, []);

    const clearAllLumpSumContributions = useCallback(() => {
        const emptyRows = defaultLumpSumContributionRows();
        lumpSumContributionsRef.current = emptyRows;
        setInputs((prev) => ({
            ...prev,
            lumpSumContributions: emptyRows,
        }));
    }, []);

    useEffect(() => {
        lumpSumWithdrawalsRef.current = normalizeLumpSumWithdrawalRows(inputs.lumpSumWithdrawals);
    }, [inputs.lumpSumWithdrawals]);

    useEffect(() => {
        lumpSumContributionsRef.current = normalizeLumpSumContributionRows(inputs.lumpSumContributions);
    }, [inputs.lumpSumContributions]);

    const handleInputChange = (key, value) => {
        // Check if trying to select a dynamic strategy when either partner is below preservation age (60)
        if (key === 'withdrawalStrategy') {
            const dynamicStrategies = ['dynamicSWR', 'vanguardGuardrails', 'floorCeiling'];
            const isDynamicStrategy = dynamicStrategies.includes(value);
            
            if (isDynamicStrategy) {
                const isCouple = inputs.relationshipStatus === 'couple';
                const yourAgeNum = Number(inputs.yourAge) || 0;
                const partnerAgeNum = Number(inputs.partnerAge) || 0;
                const preservationAge = 60;
                
                // Check if either partner is below preservation age
                const youBelowPreservation = yourAgeNum < preservationAge;
                const partnerBelowPreservation = isCouple && partnerAgeNum < preservationAge;
                
                if (youBelowPreservation || partnerBelowPreservation) {
                    const belowPreservationPartners = [];
                    if (youBelowPreservation) belowPreservationPartners.push('You');
                    if (partnerBelowPreservation) belowPreservationPartners.push('Your partner');
                    
                    alert(
                        `Dynamic withdrawal strategies can only be used when both partners have reached preservation age (60).\n\n` +
                        `${belowPreservationPartners.join(' and ')} ${belowPreservationPartners.length === 1 ? 'is' : 'are'} below preservation age.\n\n` +
                        `Please use "Fixed" strategy until both partners reach age 60.`
                    );
                    return; // Prevent strategy change
                }
            }
        }
        
        setInputs(prev => ({ ...prev, [key]: value }));
        // Keep last results visible when free runs are exhausted (no wipe-to-lock-screen).
        const freeStatus = SubscriptionManager.canRunAdvancedFree();
        if (hasCalculated && (hasAdvancedAccess || freeStatus.canRun)) {
            setHasCalculated(false);
            setResults(null);
        }
    };
    
    const sectionRefs = useRef([]);
    
    const toggleSection = (index) => {
        const newExpanded = expandedSection === index ? -1 : index;
        setExpandedSection(newExpanded);
        
        // Scroll to the section that was just opened
        if (newExpanded !== -1) {
            setTimeout(() => {
                const sectionElement = document.getElementById(`advanced-section-${index}`);
                if (sectionElement) {
                    const headerOffset = 60; // Account for sticky header
                    const elementPosition = sectionElement.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 150);
        }
    };
    
    // Tax calculation (same as Basic)
    const calculateTax = (income, { isCouple = false, hasPrivateHealthInsurance = true } = {}) => {
        let tax = 0;
        const brackets = FinancialConfig.INCOME_TAX_BRACKETS;
        for (const bracket of brackets) {
            if (income > bracket.min) {
                const taxableInBracket = Math.min(income, bracket.max) - bracket.min;
                tax += taxableInBracket * bracket.rate;
            }
        }
        
        let lito = 0;
        if (income <= 37500) lito = 700;
        else if (income <= 45000) lito = 700 - (income - 37500) * 0.05;
        else if (income <= 66667) lito = 325 - (income - 45000) * 0.015;
        tax = Math.max(0, tax - lito);
        
        const baseMedicareLevy = income * 0.02;
        let surcharge = 0;
        if (!hasPrivateHealthInsurance) {
            const singleThresholds = { tier1: 97000, tier2: 113000, tier3: 151000 };
            const familyThresholds = { tier1: 194000, tier2: 226000, tier3: 302000 };
            const thresholds = isCouple ? familyThresholds : singleThresholds;
            if (income > thresholds.tier3) surcharge = income * 0.015;
            else if (income > thresholds.tier2) surcharge = income * 0.0125;
            else if (income > thresholds.tier1) surcharge = income * 0.01;
        }
        return tax + baseMedicareLevy + surcharge;
    };

    const getAfterTaxIncome = (taxableIncome, isCouple) => {
        if (!taxableIncome || taxableIncome <= 0) return 0;
        const tax = calculateTax(taxableIncome, { isCouple, hasPrivateHealthInsurance: true });
        return Math.max(0, taxableIncome - tax);
    };

    // What-if result states
    const [whatIfResults, setWhatIfResults] = useState({
        liveToAge: null,
        moreIncome: null,
        fees: null,
        sequenceRisk: null
    });
    
    const handleCalculate = async () => {
        try {
            const yourAge = parseInt(inputs.yourAge || 0);
            const periodLength = parseInt(inputs.periodLength || 20);
            
            if (!yourAge || !periodLength) {
                alert('Please enter your age and retirement period');
                return;
            }

            if (!hasAdvancedAccess) {
                const runCheck = SubscriptionManager.canRunAdvancedFree();
                setFreeRunsStatus(runCheck);
                if (!runCheck.canRun) {
                    if (runCheck.reason === 'needs_email') {
                        setShowFreeRunsEmailGate(true);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    } else {
                        openPricingModal({
                            source: 'advanced_gate',
                            calculatorTier: 'advanced',
                            surface: 'free_runs_exhausted_run_button_mobile',
                        });
                    }
                    return;
                }
                // Free run recorded only after a successful result (Phase 1).
            }

            {
              const freeStatus = hasAdvancedAccess ? null : SubscriptionManager.canRunAdvancedFree();
              trackAdvancedRunClicked({
                platform: 'mobile',
                relationshipStatus: inputs.relationshipStatus || 'single',
                hasSubscription: hasAdvancedAccess,
                freeRunsRemaining: freeStatus?.remaining,
              });
            }
            
            // Show loading spinner - use double RAF to ensure render completes
            monteCarloRunIdRef.current += 1;
            setIsMonteCarloRunning(false);
            setIsCalculating(true);
            setCalculationMessage('Running calculations...');
            
            // Wait for React to render the spinner (double RAF ensures paint)
            await new Promise(resolve => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setTimeout(resolve, 100);
                    });
                });
            });
            
            // Clear what-if results when recalculating (desktop pattern)
            setWhatIfPeriodResult(null);
            setWhatIfFeeChangeResult(null);
            setWhatIfSeqRiskResult(null);
            
            // Use shared calculation function - EXACT desktop logic for ALL strategies
            const strategy = inputs.withdrawalStrategy || 'fixed';
            const snapStart = Number(inputs.startYear) || 1980;
            const snapPeriod = Number(inputs.periodLength) || periodLength;
            const allocSum = (Number(inputs.sp500) || 0) + (Number(inputs.ausShares) || 0)
                + (Number(inputs.intlShares) || 0) + (Number(inputs.bonds) || 0) + (Number(inputs.cash) || 0);
            // All-zero weights = unset → balanced 100% mix (and write back so the UI matches).
            let inputsForCalc = inputs;
            if (Math.abs(allocSum) < 0.5) {
                const balanced = { sp500: 35, ausShares: 15, intlShares: 20, bonds: 20, cash: 10 };
                inputsForCalc = { ...inputs, ...balanced };
                setInputs((prev) => ({ ...prev, ...balanced }));
            }
            const lumpsForRun = coerceLumpSumRowsForProjection(lumpSumWithdrawalsRef.current || inputsForCalc.lumpSumWithdrawals, {
                yourAge: inputsForCalc.yourAge,
                partnerAge: inputsForCalc.partnerAge,
                periodLength: snapPeriod,
            });
            const parsedLumps = parseLumpSumWithdrawals({ lumpSumWithdrawals: lumpsForRun });
            const lumpDiag = getLumpSumScheduleDiagnostics(
                { lumpSumWithdrawals: lumpsForRun },
                { yourAge: inputsForCalc.yourAge, partnerAge: inputsForCalc.partnerAge, periodLength: snapPeriod }
            );
            if (parsedLumps.length > 0 && lumpDiag.firingYears.length === 0) {
                setIsCalculating(false);
                alert('Lump sum schedule does not fall within this projection period. Adjust age/plan year or period length.');
                return;
            }
            const contributionsForRun = coerceLumpSumContributionRowsForProjection(lumpSumContributionsRef.current || inputsForCalc.lumpSumContributions, {
                yourAge: inputsForCalc.yourAge,
                partnerAge: inputsForCalc.partnerAge,
                periodLength: snapPeriod,
            });
            const parsedContributions = parseLumpSumContributions({ lumpSumContributions: contributionsForRun });
            const contributionDiag = getLumpSumContributionScheduleDiagnostics(
                { lumpSumContributions: contributionsForRun },
                { yourAge: inputsForCalc.yourAge, partnerAge: inputsForCalc.partnerAge, periodLength: snapPeriod }
            );
            if (parsedContributions.length > 0 && contributionDiag.firingYears.length === 0) {
                setIsCalculating(false);
                alert('Lump sum contribution schedule does not fall within this projection period. Adjust age/plan year or period length.');
                return;
            }
            const calcInputs = {
                ...inputsForCalc,
                enableBridgeStrategy: !!inputsForCalc.enableBridgeStrategy && String(strategy || 'fixed').toLowerCase() === 'fixed',
                enableMortgageComparison: !!inputsForCalc.enableMortgageComparison && String(strategy || 'fixed').toLowerCase() === 'fixed',
                msiStartsWhenEitherRetires: !!inputsForCalc.phasedRetirement
                    && String(inputsForCalc.relationshipStatus || '') === 'couple'
                    && String(inputsForCalc.msiDrawdownTrigger || 'either') !== 'primary',
                lumpSumWithdrawals: lumpsForRun,
                parsedLumpSumWithdrawals: parsedLumps,
                lumpSumContributions: contributionsForRun,
                parsedLumpSumContributions: parsedContributions,
            };

            // ONE Fixed MSI engine — main run and every rolling window
            const computeFixedMSIForWindow = (windowStart, windowLen) => {
                const windowReturns = buildHistoricalReturnsSnapshot(windowStart, windowLen, calcInputs);
                return computeFixedSustainableBinarySearch({
                    ...calcInputs,
                    periodLength: windowLen,
                    startYear: windowStart,
                    providedReturns: windowReturns,
                });
            };

            const buildWindowCalcInputs = (windowStart, windowLen) => ({
                ...calcInputs,
                periodLength: windowLen,
                startYear: windowStart,
                providedReturns: buildHistoricalReturnsSnapshot(windowStart, windowLen, calcInputs),
            });

            // Use NEW desktop functions (single source of truth)
            let result;
            switch(strategy.toLowerCase()) {
                case 'fixed': {
                    const { rolling, current } = computeRollingPeriodAnalysis({
                        selectedStartYear: snapStart,
                        windowLen: snapPeriod,
                        totalSuper: (Number(inputsForCalc.yourSuper) || 0) + (Number(inputsForCalc.partnerSuper) || 0),
                        computeForWindow: (s, len) => computeFixedMSIForWindow(s, len),
                    });
                    const fixedFromRolling = current?.fullResult || computeFixedMSIForWindow(snapStart, snapPeriod);
                    result = {
                        sustainableIncome: fixedFromRolling.avgIncome,
                        avgIncome: fixedFromRolling.avgIncome,
                        finalBalance: fixedFromRolling.finalBalance,
                        incomeByYear: fixedFromRolling.incomeByYear,
                        fixedIncomeVaries: !!fixedFromRolling.fixedIncomeVaries,
                        minimumYearIncome: fixedFromRolling.minimumYearIncome,
                    };
                    setRollingMSI(rolling.map(({ start: periodStart, income }) => ({ start: periodStart, income })));
                    break;
                }
                case 'vanguardguardrails':
                    const vgResult = computeVanguardGuardrailsSeries(buildWindowCalcInputs(snapStart, snapPeriod));
                    result = {
                        sustainableIncome: vgResult.avgIncome,
                        avgIncome: vgResult.avgIncome,
                        finalBalance: vgResult.finalBalance,
                        incomeByYear: vgResult.incomeByYear
                    };
                    break;
                case 'dynamicswr':
                    const dynResult = computeDynamicSWRSeries(buildWindowCalcInputs(snapStart, snapPeriod));
                    result = {
                        sustainableIncome: dynResult.avgIncome,
                        avgIncome: dynResult.avgIncome,
                        finalBalance: dynResult.finalBalance,
                        incomeByYear: dynResult.incomeByYear
                    };
                    break;
                case 'floorceiling':
                    const fcResult = computeFloorCeilingSeries(buildWindowCalcInputs(snapStart, snapPeriod));
                    result = {
                        sustainableIncome: fcResult.avgIncome,
                        avgIncome: fcResult.avgIncome,
                        finalBalance: fcResult.finalBalance,
                        incomeByYear: fcResult.incomeByYear
                    };
                    break;
                default:
                    alert('Unknown withdrawal strategy: ' + strategy);
                    return;
            }
            
            // Rolling period analysis (Fixed: already computed above in same loop as main MSI)
            if (strategy.toLowerCase() !== 'fixed') {
                const totalSuperRolling = (Number(inputs.yourSuper) || 0) + (Number(inputs.partnerSuper) || 0);
                const { rolling } = computeRollingPeriodAnalysis({
                    selectedStartYear: snapStart,
                    windowLen: periodLength,
                    totalSuper: totalSuperRolling,
                    computeForWindow: (s, len) => {
                        switch (strategy.toLowerCase()) {
                            case 'vanguardguardrails':
                                return computeVanguardGuardrailsSeries(buildWindowCalcInputs(s, len)).avgIncome;
                            case 'dynamicswr':
                                return computeDynamicSWRSeries(buildWindowCalcInputs(s, len)).avgIncome;
                            case 'floorceiling':
                                return computeFloorCeilingSeries(buildWindowCalcInputs(s, len)).avgIncome;
                            default:
                                return computeFixedMSIForWindow(s, len).avgIncome;
                        }
                    },
                });
                setRollingMSI(rolling.map(({ start: periodStart, income }) => ({ start: periodStart, income })));
            }

            // Snapshot for What-If (single source of truth — same as desktop Run)
            lastReturnsByYearRef.current = buildHistoricalReturnsSnapshot(snapStart, snapPeriod, calcInputs);
            lastRunInputsRef.current = buildWindowCalcInputs(snapStart, snapPeriod);
            lastRunStrategyRef.current = strategy;
            currentStrategyMSIRef.current = Math.round(result.sustainableIncome || result.avgIncome || 0);

            const headlineMSI = Math.round(result.sustainableIncome || result.avgIncome || 0);
            setFixedIncomeVariesNote(
                String(strategy).toLowerCase() === 'fixed'
                    ? buildFixedIncomeVariesNote(result, snapStart, snapPeriod)
                    : null
            );
            const deferredMonteCarlo = {
                ...calcInputs,
                monteCarloTargetIncome: Number(calcInputs.monteCarloTargetIncome) || headlineMSI,
            };

            setResults({
                finalBalance: result.finalBalance,
                maxSustainableIncome: headlineMSI,
                avgIncome: headlineMSI,
                successRate: null,
                chartData: result.incomeByYear.map(d => ({
                    ...d,
                    endingBalance: d.balance,
                    age: d.yearIndex + yourAge - 1
                })),
                params: {
                    totalSuper: (Number(inputs.yourSuper) || 0) + (Number(inputs.partnerSuper) || 0),
                    periodLength: inputs.periodLength,
                    yourAge: inputs.yourAge,
                    startYear: inputs.startYear
                }
            });

            if (!hasAdvancedAccess) {
              SubscriptionManager.recordAdvancedRun();
              setFreeRunsStatus(SubscriptionManager.canRunAdvancedFree());
            }

            trackAdvancedRunSucceeded({
              platform: 'mobile',
              relationshipStatus: inputs.relationshipStatus || 'single',
              sustainableIncome: headlineMSI,
            });

            setHasCalculated(true);
            setIsCalculating(false);
            setCalculationMessage('');

            // Wait until results exist, then instant-scroll to results TOP (not charts)
            let scrollTarget = null;
            const scrollDeadline = Date.now() + 2500;
            while (Date.now() < scrollDeadline) {
              const resultsTop = document.getElementById('simulation-results')
                || document.getElementById('balance-chart-container');
              if (resultsTop) {
                scrollTarget = resultsTop;
                break;
              }
              await new Promise((resolve) => setTimeout(resolve, 16));
            }
            if (!scrollTarget) {
              scrollTarget = resultsRef.current;
            }
            if (scrollTarget) {
              const yOffset = window.pageYOffset || document.documentElement.scrollTop || 0;
              const top = Math.max(0, scrollTarget.getBoundingClientRect().top + yOffset - 80);
              const se = document.scrollingElement || document.documentElement;
              if (se) se.scrollTop = top;
              if (document.body) document.body.scrollTop = top;
              window.scrollTo(0, top);
            }

            // Chunked async MC after paint — yields so page can scroll while MC runs
            const mcRunId = ++monteCarloRunIdRef.current;
            const mcInputs = deferredMonteCarlo;
            setIsMonteCarloRunning(true);
            setTimeout(async () => {
              if (monteCarloRunIdRef.current !== mcRunId) return;
              try {
                const mcResults = await runMonteCarloSimulationAsync(mcInputs, {
                  shouldCancel: () => monteCarloRunIdRef.current !== mcRunId,
                });
                if (monteCarloRunIdRef.current !== mcRunId) return;
                setMonteCarloResults(mcResults);
                setResults((prev) => (prev ? { ...prev, successRate: mcResults.successRate } : prev));
              } catch (error) {
                console.error('Monte Carlo calculation error:', error);
              } finally {
                if (monteCarloRunIdRef.current === mcRunId) {
                  setIsMonteCarloRunning(false);
                }
              }
            }, 200);
            
        } catch (error) {
            console.error('Calculation error:', error);
            setIsCalculating(false);
            setIsMonteCarloRunning(false);
            alert(`Error: ${error.message}. Please check all inputs.`);
        }
    };
    
    // What-If handlers — single source of truth via whatIfEngine.js (same as desktop)
    const handleChangeRetirementPeriod = async () => {
        if (!hasCalculated || !currentStrategyMSIRef.current) {
            setWhatIfPeriodResult({ success: false, error: 'Please run the main calculation first' });
            return;
        }
        const validatedPeriod = validateInput(whatIfPeriod, 1, 100, 0);
        if (validatedPeriod <= 0) {
            setWhatIfPeriodResult({ success: false, error: 'Please enter a valid retirement period (1-100 years)' });
            return;
        }
        const start = Number(inputs.startYear) || 1980;
        const endYear = start + validatedPeriod - 1;
        const maxDataYear = Math.max(...Object.keys(historicalData).map(Number));
        if (endYear > maxDataYear) {
            setWhatIfPeriodResult({ success: false, error: `Period extends beyond available data (through ${maxDataYear})` });
            return;
        }
        const result = await calculateWhatIfScenario('Change Retirement Period', async () => {
            const lastInputs = lastRunInputsRef.current || inputs;
            const lastReturns = lastReturnsByYearRef.current || [];
            const originalPeriod = Math.max(0, Number(lastInputs.periodLength) || 0);
            const strategy = (String(lastRunStrategyRef.current || inputs.withdrawalStrategy) || 'fixed').toLowerCase();
            if (validatedPeriod === originalPeriod) {
                const exact = currentStrategyMSIRef.current;
                return { avgIncome: exact, survivalYears: validatedPeriod, currentMaxSustainableIncome: exact, impact: 0 };
            }
            const baselineInputs = buildBaselineCalcInputs(lastInputs, lastReturns);
            const whatIfInputs = buildWhatIfCalcInputs(lastInputs, lastReturns, { periodLength: validatedPeriod });
            const baselineIncome = computeStrategyMSI(baselineInputs, strategy);
            const newIncome = computeStrategyMSI(whatIfInputs, strategy);
            return { avgIncome: newIncome, survivalYears: validatedPeriod, currentMaxSustainableIncome: baselineIncome, impact: newIncome - baselineIncome };
        });
        setWhatIfPeriodResult(result);
    };

    const handleChangeAnnualFees = async () => {
        if (!hasCalculated || !currentStrategyMSIRef.current) {
            setWhatIfFeeChangeResult({ success: false, error: 'Please run the main calculation first' });
            return;
        }
        const validatedFeeChange = validateInput(whatIfFeeChangePct, -10, 10, 0);
        if (validatedFeeChange === 0 && whatIfFeeChangePct !== '0') {
            setWhatIfFeeChangeResult({ success: false, error: 'Please enter a valid fee change (-10% to +10%)' });
            return;
        }
        const result = await calculateWhatIfScenario('Change Annual Fees', async () => {
            const lastInputs = lastRunInputsRef.current || inputs;
            const lastReturns = lastReturnsByYearRef.current || [];
            const strategy = (String(lastRunStrategyRef.current || inputs.withdrawalStrategy) || 'fixed').toLowerCase();
            if (validatedFeeChange === 0) {
                const exact = currentStrategyMSIRef.current;
                return { avgIncome: exact, feeChange: 0, currentMaxSustainableIncome: exact, impact: 0 };
            }
            const baseFees = Number(lastInputs.investmentFees ?? inputs.investmentFees) || 0;
            const newFees = Math.max(0, baseFees + validatedFeeChange);
            const baselineInputs = buildBaselineCalcInputs(lastInputs, lastReturns);
            const whatIfInputs = buildWhatIfCalcInputs(lastInputs, lastReturns, { investmentFees: newFees });
            const baselineIncome = computeStrategyMSI(baselineInputs, strategy);
            const newIncome = computeStrategyMSI(whatIfInputs, strategy);
            return { avgIncome: newIncome, feeChange: validatedFeeChange, currentMaxSustainableIncome: baselineIncome, impact: newIncome - baselineIncome };
        });
        setWhatIfFeeChangeResult(result);
    };

    const handleSequenceRisk = async () => {
        if (!hasCalculated || !currentStrategyMSIRef.current) {
            setWhatIfSeqRiskResult({ success: false, error: 'Please run the main calculation first' });
            return;
        }
        if (!whatIfSeqRiskPct || whatIfSeqRiskPct === '' || whatIfSeqRiskPct === '-') {
            setWhatIfSeqRiskResult({ success: false, error: 'Please enter a valid reduction (-95 to 0 percentage points)' });
            return;
        }
        const validatedSeqRisk = validateInput(whatIfSeqRiskPct, -95, 0, 0);
        const result = await calculateWhatIfScenario('Sequence Risk', async () => {
            const lastInputs = lastRunInputsRef.current || inputs;
            const lastReturns = lastReturnsByYearRef.current || [];
            const strategy = (String(lastRunStrategyRef.current || inputs.withdrawalStrategy) || 'fixed').toLowerCase();
            const baselineReturns = resolveBaselineReturns(lastInputs, lastReturns);
            const seqReturns = applySequenceRiskToReturns(baselineReturns, validatedSeqRisk, 3);
            const baselineInputs = buildBaselineCalcInputs(lastInputs, baselineReturns);
            const whatIfInputs = buildWhatIfCalcInputs(lastInputs, baselineReturns, { providedReturns: seqReturns });
            const baselineIncome = computeStrategyMSI(baselineInputs, strategy);
            const whatIfResult = computeStrategyFullResult(whatIfInputs, strategy);
            const newIncome = Math.round(whatIfResult.avgIncome || 0);
            return {
                avgIncome: newIncome,
                balanceAfter3Years: Math.round(whatIfResult.incomeByYear?.[2]?.balance ?? 0),
                sequenceRiskPct: validatedSeqRisk,
                currentMaxSustainableIncome: baselineIncome,
                impact: newIncome - baselineIncome,
            };
        });
        setWhatIfSeqRiskResult(result);
    };
    
    // Compute strategy options based on preservation age
    const isCoupleMobile = inputs.relationshipStatus === 'couple';
    const yourAgeNumMobile = Number(inputs.yourAge) || 0;
    const partnerAgeNumMobile = Number(inputs.partnerAge) || 0;
    const preservationAgeMobile = 60;
    const youBelowPreservationMobile = yourAgeNumMobile < preservationAgeMobile;
    const partnerBelowPreservationMobile = isCoupleMobile && partnerAgeNumMobile < preservationAgeMobile;
    const eitherBelowPreservationMobile = youBelowPreservationMobile || partnerBelowPreservationMobile;
    
    const strategyOptionsMobile = [
        { value: 'fixed', label: 'Fixed' },
        { 
            value: 'dynamicSWR', 
            label: eitherBelowPreservationMobile ? 'Dynamic SWR (Not available)' : 'Dynamic SWR',
            disabled: eitherBelowPreservationMobile
        },
        { 
            value: 'vanguardGuardrails', 
            label: eitherBelowPreservationMobile ? 'Vanguard Guardrails (Not available)' : 'Vanguard Guardrails',
            disabled: eitherBelowPreservationMobile
        },
        { 
            value: 'floorCeiling', 
            label: eitherBelowPreservationMobile ? 'Floor & Ceiling (Not available)' : 'Floor & Ceiling',
            disabled: eitherBelowPreservationMobile
        }
    ];
    const bridgeStrategyApplies = String(inputs.withdrawalStrategy || 'fixed').toLowerCase() === 'fixed';
    
    // FULL SECTION DEFINITIONS - ALL ADVANCED FIELDS
    const sections = [
        {
            id: 0,
            title: 'Personal Details',
            icon: '👤',
            subtitle: `${inputs.relationshipStatus === 'couple' ? 'Couple' : 'Single'}, Age ${inputs.yourAge || '?'}`,
            fields: [
                {
                    key: 'relationshipStatus',
                    label: 'Relationship Status',
                    type: 'select',
                    options: [
                        { value: 'single', label: 'Single' },
                        { value: 'couple', label: 'Couple' }
                    ]
                },
                {
                    key: 'yourAge',
                    label: 'Your Age',
                    type: 'number',
                    min: 18,
                    max: 120
                },
                {
                    key: 'yourGender',
                    label: 'Your Gender',
                    type: 'select',
                    options: [
                        { value: 'male', label: 'Male' },
                        { value: 'female', label: 'Female' }
                    ]
                },
                {
                    key: 'yourSuper',
                    label: 'Your Super Balance ($)',
                    type: 'currency',
                    min: 0
                },
                {
                    key: 'yourTbcUsed',
                    label: 'Your Transfer Balance Cap Used ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Amount already in pension phase'
                },
                {
                    key: 'partnerAge',
                    label: "Partner's Age",
                    type: 'number',
                    min: 18,
                    max: 120,
                    showIf: () => inputs.relationshipStatus === 'couple'
                },
                {
                    key: 'partnerGender',
                    label: "Partner's Gender",
                    type: 'select',
                    options: [
                        { value: 'male', label: 'Male' },
                        { value: 'female', label: 'Female' }
                    ],
                    showIf: () => inputs.relationshipStatus === 'couple'
                },
                {
                    key: 'partnerSuper',
                    label: "Partner's Super Balance ($)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.relationshipStatus === 'couple'
                },
                {
                    key: 'partnerTbcUsed',
                    label: "Partner's TBC Used ($)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.relationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 1,
            title: 'Retirement Plan',
            icon: '🎯',
            subtitle: `${inputs.periodLength || '?'} years`,
            fields: [
                {
                    key: 'periodLength',
                    label: 'Retirement Period (years)',
                    type: 'number',
                    min: 1,
                    max: 60,
                    helper: 'How many years in retirement'
                },
                {
                    key: 'targetFinalBalance',
                    label: 'Target Final Balance ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Desired balance at end of retirement',
                    disabled: inputs.withdrawalStrategy === 'FloorAndCeiling',
                    warning: inputs.withdrawalStrategy === 'FloorAndCeiling' ? '⚠️ Not available for Floor & Ceiling strategy. This strategy is designed for portfolio longevity, not depleting to a specific target. Fixed strategy supports target-based planning.' : null
                },
                {
                    key: 'startYear',
                    label: 'Start Year',
                    type: 'number',
                    min: 1900,
                    max: 2100,
                    helper: 'First year of historical data to use'
                }
            ]
        },
        {
            id: 2,
            title: 'Phased Retirement',
            icon: '⏳',
            subtitle: inputs.phasedRetirement ? 'Enabled' : 'Disabled',
            fields: [
                {
                    key: 'phasedRetirement',
                    label: 'Enable Phased Retirement',
                    type: 'checkbox',
                    helper: 'Work part-time while drawing super'
                },
                {
                    key: 'msiDrawdownTrigger',
                    label: 'Super drawdown starts (Fixed MSI)',
                    type: 'select',
                    helper: 'When either partner retires, or only when you (primary) retire',
                    showIf: () => inputs.phasedRetirement && inputs.relationshipStatus === 'couple',
                    options: [
                        { value: 'either', label: 'When either partner retires' },
                        { value: 'primary', label: 'Only when you (primary) retire' },
                    ],
                },
                {
                    key: 'yourRetirementAge',
                    label: 'Your Retirement Age',
                    type: 'number',
                    min: 55,
                    max: 75,
                    helper: 'Used for phased retirement and the drawdown trigger',
                    showIf: () => inputs.phasedRetirement && inputs.relationshipStatus === 'couple'
                },
                {
                    key: 'partnerRetirementAge',
                    label: "Partner's Retirement Age",
                    type: 'number',
                    min: 55,
                    max: 75,
                    helper: 'Used for phased retirement and the drawdown trigger',
                    showIf: () => inputs.phasedRetirement && inputs.relationshipStatus === 'couple'
                },
                {
                    key: 'yourSalary',
                    label: 'Your Annual Salary ($/year)',
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.phasedRetirement
                },
                {
                    key: 'yourSuperContributions',
                    label: 'Your Super Contributions ($/year)',
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.phasedRetirement
                },
                {
                    key: 'partnerSalary',
                    label: "Partner's Annual Salary ($/year)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.phasedRetirement && inputs.relationshipStatus === 'couple'
                },
                {
                    key: 'partnerSuperContributions',
                    label: "Partner's Super Contributions ($/year)",
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.phasedRetirement && inputs.relationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 21,
            title: 'Bridge / Delayed Super Access',
            icon: '🌉',
            subtitle: !bridgeStrategyApplies ? 'Fixed only' : (inputs.enableBridgeStrategy ? 'Enabled' : 'Optional'),
            fields: [
                {
                    key: 'enableBridgeStrategy',
                    label: 'Enable Bridge Strategy',
                    type: 'checkbox',
                    helper: 'Use cash and outside-super assets before drawing super. Currently available for Fixed Sustainable Income only.',
                    warning: !bridgeStrategyApplies ? 'Bridge / delayed super access is currently modelled in the Fixed strategy only.' : null
                },
                {
                    key: 'yourSuperAccessAge',
                    label: 'Your Super Access Age',
                    type: 'number',
                    min: 60,
                    max: 100,
                    showIf: () => inputs.enableBridgeStrategy
                },
                {
                    key: 'partnerSuperAccessAge',
                    label: "Partner's Super Access Age",
                    type: 'number',
                    min: 60,
                    max: 100,
                    showIf: () => inputs.enableBridgeStrategy && inputs.relationshipStatus === 'couple'
                },
                {
                    key: 'bridgeCashBucket',
                    label: 'Cash Bucket ($)',
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.enableBridgeStrategy
                },
                {
                    key: 'bridgeInvestmentBucket',
                    label: 'Outside-Super Investment Bucket ($)',
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.enableBridgeStrategy
                },
                {
                    key: 'bridgeInvestmentReturnRate',
                    label: 'Outside-Super Real Return (%)',
                    type: 'number',
                    step: 0.1,
                    showIf: () => inputs.enableBridgeStrategy
                }
            ]
        },
        {
            id: 22,
            title: 'Mortgage Payoff Strategy Comparison',
            icon: '🏠',
            subtitle: String(inputs.withdrawalStrategy || 'fixed').toLowerCase() !== 'fixed'
                ? 'Fixed only'
                : (inputs.enableMortgageComparison ? 'Enabled' : 'Optional'),
            fields: [
                {
                    key: 'enableMortgageComparison',
                    label: 'Enable Mortgage Comparison',
                    type: 'checkbox',
                    helper: 'Compare keeping repayments, paying off the loan from super or outside cash, and partial payoff. Outside-cash payoff can also be compared with contributing that outside cash to super. Fixed Sustainable Income only.'
                },
                {
                    key: 'mortgageBalance',
                    label: 'Mortgage Balance ($)',
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.enableMortgageComparison
                },
                {
                    key: 'mortgageInterestRate',
                    label: 'Interest Rate (%)',
                    type: 'number',
                    step: 0.1,
                    min: 0,
                    showIf: () => inputs.enableMortgageComparison
                },
                {
                    key: 'mortgageAnnualRepayment',
                    label: 'Annual Repayments ($)',
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.enableMortgageComparison
                },
                {
                    key: 'mortgageStrategy',
                    label: 'Main Strategy',
                    type: 'select',
                    options: [
                        { value: 'keep', label: 'Keep paying' },
                        { value: 'immediatePayoff', label: 'Pay off immediately' },
                        { value: 'laterPayoff', label: 'Pay off later' },
                        { value: 'partialPayoff', label: 'Partial payoff' }
                    ],
                    showIf: () => inputs.enableMortgageComparison
                },
                {
                    key: 'mortgagePayoffSource',
                    label: 'Payoff Funding Source',
                    type: 'select',
                    options: [
                        { value: 'super', label: 'Super lump sum withdrawal' },
                        { value: 'general', label: 'General retirement cashflow' }
                    ],
                    showIf: () => inputs.enableMortgageComparison
                },
                {
                    key: 'mortgagePayoffScheduleType',
                    label: 'Schedule By',
                    type: 'select',
                    options: [
                        { value: 'year', label: 'Plan year' },
                        { value: 'age', label: 'Your age' }
                    ],
                    showIf: () => inputs.enableMortgageComparison
                },
                {
                    key: 'mortgagePayoffScheduleValue',
                    label: 'Payoff Age / Year',
                    type: 'number',
                    min: 1,
                    showIf: () => inputs.enableMortgageComparison
                },
                {
                    key: 'mortgagePartialPayoffAmount',
                    label: 'Partial Payoff Amount ($)',
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.enableMortgageComparison
                }
            ]
        },
        {
            id: 3,
            title: 'Other Income & Assets',
            icon: '💰',
            subtitle: `$${(parseFloat(inputs.otherTaxableIncome || 0) + parseFloat(inputs.otherNonTaxableIncome || 0)).toLocaleString()}/yr`,
            fields: [
                {
                    key: 'otherTaxableIncome',
                    label: 'Other Taxable Income ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Rental income, part-time work, etc.'
                },
                {
                    key: 'otherNonTaxableIncome',
                    label: 'Other Non-Taxable Income ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Tax-exempt income sources'
                },
                {
                    key: 'otherAssets',
                    label: 'Assessable Assets ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Assets outside super for pension means test'
                },
                {
                    key: 'isHomeowner',
                    label: 'Homeowner',
                    type: 'checkbox',
                    helper: 'Affects Age Pension asset test'
                },
                {
                    key: 'includePension',
                    label: 'Include Age Pension',
                    type: 'checkbox',
                    helper: 'Calculate Age Pension eligibility'
                },
                {
                    key: 'pensionGrowthRate',
                    label: 'Pension Growth Rate (%/year)',
                    type: 'percentage',
                    min: 0,
                    max: 10,
                    step: 0.1,
                    helper: 'Indexes future pension rates and means-test thresholds together (default 0.3%)',
                    showIf: () => inputs.includePension
                }
            ]
        },
        {
            id: 4,
            title: 'Annuities & DB Pensions',
            icon: '📜',
            subtitle: inputs.annuityPurchasePrice ? `Annuity: $${parseFloat(inputs.annuityPurchasePrice || 0).toLocaleString()}` : 'None',
            fields: [
                {
                    key: 'annuityPurchasePrice',
                    label: 'Annuity Purchase Price ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'One-time cost to purchase annuity'
                },
                {
                    key: 'annuityAnnualPayment',
                    label: 'Annuity Annual Payment ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Annual income from annuity'
                },
                {
                    key: 'annuityIsIndexed',
                    label: 'Annuity is CPI Indexed',
                    type: 'checkbox'
                },
                {
                    key: 'definedBenefitPensionAmount',
                    label: 'Defined Benefit Pension ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Annual DB pension income'
                },
                {
                    key: 'definedBenefitIsIndexed',
                    label: 'DB Pension is CPI Indexed',
                    type: 'checkbox'
                }
            ]
        },
        {
            id: 5,
            title: 'Health Insurance',
            icon: '🏥',
            subtitle: inputs.hasPrivateHealthInsurance ? 'Private cover' : 'No private cover',
            fields: [
                {
                    key: 'hasPrivateHealthInsurance',
                    label: 'You Have Private Health Insurance',
                    type: 'checkbox',
                    helper: 'Affects Medicare Levy Surcharge'
                },
                {
                    key: 'partnerHasPrivateHealthInsurance',
                    label: 'Partner Has Private Health Insurance',
                    type: 'checkbox',
                    showIf: () => inputs.relationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 6,
            title: 'Portfolio Allocation',
            icon: '📊',
            subtitle: `${inputs.sp500 + inputs.ausShares + inputs.intlShares}% equities`,
            fields: [
                {
                    key: 'sp500',
                    label: 'S&P 500 (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    helper: 'US large cap stocks'
                },
                {
                    key: 'ausShares',
                    label: 'Australian Shares (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100
                },
                {
                    key: 'intlShares',
                    label: 'International Shares (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    helper: 'ex-US international equities'
                },
                {
                    key: 'bonds',
                    label: 'Bonds (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100
                },
                {
                    key: 'cash',
                    label: 'Cash (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100
                },
                {
                    key: 'infrastructurePct',
                    label: 'Infrastructure / Alternatives (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    helper: 'Transferred from SMSF alternatives'
                },
                {
                    key: 'frankingCredits',
                    label: 'Franking Credits (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    helper: 'Percentage of Aus dividends franked'
                }
            ]
        },
        {
            id: 6.5,
            title: 'Alternative Assets',
            icon: '🏢',
            subtitle: inputs.transferFromSMSF ? 'From SMSF' : 'Manual Entry',
            fields: [
                {
                    key: 'residentialPropertyValue',
                    label: 'Residential Property Value ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Current market value of residential properties'
                },
                {
                    key: 'residentialRentalIncome',
                    label: 'Residential Rental Income ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Annual rental income from residential properties'
                },
                {
                    key: 'residentialPropertyRentalYield',
                    label: 'Residential Rental Yield (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    step: 0.1,
                    helper: 'Annual rental yield percentage'
                },
                {
                    key: 'residentialPropertyCapitalGrowth',
                    label: 'Residential Capital Growth (%)',
                    type: 'percentage',
                    min: -20,
                    max: 20,
                    step: 0.1,
                    helper: 'Expected annual capital growth rate'
                },
                {
                    key: 'commercialPropertyValue',
                    label: 'Commercial Property Value ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Current market value of commercial properties'
                },
                {
                    key: 'commercialRentalIncome',
                    label: 'Commercial Rental Income ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Annual rental income from commercial properties'
                },
                {
                    key: 'commercialPropertyRentalYield',
                    label: 'Commercial Rental Yield (%)',
                    type: 'percentage',
                    min: 0,
                    max: 100,
                    step: 0.1,
                    helper: 'Annual rental yield percentage'
                },
                {
                    key: 'commercialPropertyCapitalGrowth',
                    label: 'Commercial Capital Growth (%)',
                    type: 'percentage',
                    min: -20,
                    max: 20,
                    step: 0.1,
                    helper: 'Expected annual capital growth rate'
                },
                {
                    key: 'collectiblesValue',
                    label: 'Collectibles Value ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Art, antiques, rare items'
                },
                {
                    key: 'collectiblesCapitalGrowth',
                    label: 'Collectibles Capital Growth (%)',
                    type: 'percentage',
                    min: -20,
                    max: 20,
                    step: 0.1,
                    helper: 'Expected annual capital growth rate'
                },
                {
                    key: 'otherAlternativesValue',
                    label: 'Other Alternatives Value ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Crypto, commodities, other alternatives'
                },
                {
                    key: 'otherAlternativesCapitalGrowth',
                    label: 'Other Alternatives Capital Growth (%)',
                    type: 'percentage',
                    min: -20,
                    max: 20,
                    step: 0.1,
                    helper: 'Expected annual capital growth rate'
                }
            ]
        },
        {
            id: 7,
            title: 'Withdrawal Strategy',
            icon: '💸',
            subtitle: inputs.withdrawalStrategy === 'vanguardGuardrails' ? 'Vanguard Guardrails' : 
                     inputs.withdrawalStrategy === 'fixed' ? 'Fixed' :
                     inputs.withdrawalStrategy === 'monteCarlo' ? 'Monte Carlo' : 'Dynamic SWR',
            fields: [
                {
                    key: 'withdrawalStrategy',
                    label: 'Strategy',
                    type: 'select',
                    options: strategyOptionsMobile
                },
                {
                    key: 'monteCarloTargetIncome',
                    label: 'Target Income ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Desired annual income for Fixed strategy',
                    showIf: () => inputs.withdrawalStrategy === 'fixed'
                }
            ]
        },
        {
            id: 8,
            title: 'Lump Sum Withdrawals',
            icon: '💰',
            subtitle: (() => {
                const n = (inputs.lumpSumWithdrawals || []).filter(
                    (r) => Number(r.amount) > 0 && r.scheduleValue !== '' && r.scheduleValue != null
                ).length;
                return n > 0 ? `${n} scheduled` : 'Optional — tap to add';
            })(),
            fields: [],
            customContent: () => {
                const rows = normalizeLumpSumWithdrawalRows(inputs.lumpSumWithdrawals);
                const visibleRows = getVisibleLumpSumRows(rows);
                const hasScheduledRows = parseLumpSumWithdrawals({ lumpSumWithdrawals: rows }).length > 0;
                return (
                    <div className="space-y-3">
                        <p className="text-xs text-amber-800 leading-relaxed">
                            One-off super draws on top of strategy income. MSI is recalculated to include these. Use age or plan year (year 1 = first year of projection).
                        </p>
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-xs text-amber-900">
                                {hasScheduledRows ? `${parseLumpSumWithdrawals({ lumpSumWithdrawals: rows }).length} scheduled` : 'No lump sum withdrawals scheduled.'}
                            </p>
                            <button
                                type="button"
                                onClick={clearAllLumpSumWithdrawals}
                                disabled={!hasScheduledRows}
                                className="text-xs font-medium px-3 py-2 rounded border border-amber-300 text-amber-900 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Clear all
                            </button>
                        </div>
                        {visibleRows.slice(0, MAX_LUMP_SUM_WITHDRAWALS).map(({ row, idx }) => (
                            <div key={`lump-sum-mobile-${idx}`} className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-xs font-semibold text-amber-900">Withdrawal {idx + 1}</div>
                                    <button
                                        type="button"
                                        onClick={() => updateLumpSumRow(idx, { scheduleType: 'year', scheduleValue: '', amount: '', label: '' })}
                                        disabled={String(row.scheduleValue ?? '').trim() === '' && String(row.amount ?? '').trim() === '' && String(row.label ?? '').trim() === ''}
                                        className="text-xs px-2 py-1 rounded border border-amber-200 text-amber-900 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-amber-900 mb-1">When</label>
                                    <select
                                        value={row.scheduleType || 'year'}
                                        onChange={(e) => updateLumpSumRow(idx, { scheduleType: e.target.value })}
                                        className="w-full px-3 py-3 text-base border-2 border-amber-200 rounded-lg bg-white"
                                        style={{ fontSize: '16px' }}
                                    >
                                        <option value="age">Age</option>
                                        <option value="year">Plan year</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-amber-900 mb-1">
                                        {row.scheduleType === 'year' ? 'Year #' : 'Age'}
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={row.scheduleValue ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const scheduleValue = Number(value);
                                            const minCurrentAge = Math.min(Number(inputs.yourAge) || Infinity, Number(inputs.partnerAge ?? inputs.yourAge) || Infinity);
                                            const shouldTreatAsPlanYear = (row.scheduleType || 'year') === 'age'
                                                && Number.isFinite(scheduleValue)
                                                && scheduleValue > 0
                                                && scheduleValue <= Number(inputs.periodLength || 0)
                                                && scheduleValue < minCurrentAge;
                                            updateLumpSumRow(idx, {
                                                scheduleValue: value,
                                                scheduleType: shouldTreatAsPlanYear ? 'year' : (row.scheduleType || 'year'),
                                            });
                                        }}
                                        placeholder={(row.scheduleType || 'year') === 'year' ? '4' : String(inputs.yourAge || 64)}
                                        className="w-full px-4 py-3 text-base border-2 border-amber-200 rounded-lg"
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-amber-900 mb-1">Amount ($)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1000"
                                        value={row.amount ?? ''}
                                        onChange={(e) => updateLumpSumRow(idx, { amount: e.target.value })}
                                        placeholder="40000"
                                        className="w-full px-4 py-3 text-base border-2 border-amber-200 rounded-lg"
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-amber-900 mb-1">Label (optional)</label>
                                    <input
                                        type="text"
                                        value={row.label ?? ''}
                                        onChange={(e) => updateLumpSumRow(idx, { label: e.target.value })}
                                        placeholder="Caravan, renovation, help children"
                                        className="w-full px-4 py-3 text-base border-2 border-amber-200 rounded-lg"
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            id: 9,
            title: 'Lump Sum Contributions',
            icon: '➕',
            subtitle: (() => {
                const n = (inputs.lumpSumContributions || []).filter(
                    (r) => Number(r.amount) > 0 && r.scheduleValue !== '' && r.scheduleValue != null
                ).length;
                return n > 0 ? `${n} scheduled` : 'Optional — tap to add';
            })(),
            fields: [],
            customContent: () => {
                const rows = normalizeLumpSumContributionRows(inputs.lumpSumContributions);
                const visibleRows = getVisibleLumpSumContributionRows(rows);
                const hasScheduledRows = parseLumpSumContributions({ lumpSumContributions: rows }).length > 0;
                return (
                    <div className="space-y-3">
                        <p className="text-xs text-emerald-800 leading-relaxed">
                            One-off payments into super. Choose recipient and type; phased retirement SG counts toward concessional cap checks.
                        </p>
                        <div className="flex items-center justify-between gap-3">
                            <p className="text-xs text-emerald-900">
                                {hasScheduledRows ? `${parseLumpSumContributions({ lumpSumContributions: rows }).length} scheduled` : 'No lump sum contributions scheduled.'}
                            </p>
                            <button
                                type="button"
                                onClick={clearAllLumpSumContributions}
                                disabled={!hasScheduledRows}
                                className="text-xs font-medium px-3 py-2 rounded border border-emerald-300 text-emerald-900 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Clear all
                            </button>
                        </div>
                        {visibleRows.map(({ row, idx }) => (
                            <div key={`lump-contribution-mobile-${idx}`} className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-xs font-semibold text-emerald-900">Contribution {idx + 1}</div>
                                    <button
                                        type="button"
                                        onClick={() => updateLumpSumContributionRow(idx, { recipient: 'you', contributionType: 'nonConcessional', scheduleType: 'year', scheduleValue: '', amount: '', label: '' })}
                                        disabled={String(row.scheduleValue ?? '').trim() === '' && String(row.amount ?? '').trim() === '' && String(row.label ?? '').trim() === ''}
                                        className="text-xs px-2 py-1 rounded border border-emerald-200 text-emerald-900 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                                    >
                                        Clear
                                    </button>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-emerald-900 mb-1">Recipient</label>
                                    <select
                                        value={row.recipient || 'you'}
                                        onChange={(e) => updateLumpSumContributionRow(idx, { recipient: e.target.value })}
                                        className="w-full px-3 py-3 text-base border-2 border-emerald-200 rounded-lg bg-white"
                                        style={{ fontSize: '16px' }}
                                    >
                                        <option value="you">You</option>
                                        {inputs.relationshipStatus === 'couple' && <option value="partner">Partner</option>}
                                        {inputs.relationshipStatus === 'couple' && <option value="split">Split 50/50</option>}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-emerald-900 mb-1">Contribution type</label>
                                    <select
                                        value={row.contributionType || 'nonConcessional'}
                                        onChange={(e) => updateLumpSumContributionRow(idx, { contributionType: e.target.value })}
                                        className="w-full px-3 py-3 text-base border-2 border-emerald-200 rounded-lg bg-white"
                                        style={{ fontSize: '16px' }}
                                    >
                                        <option value="nonConcessional">Non-concessional</option>
                                        <option value="concessional">Concessional / personal deductible</option>
                                        <option value="downsizer">Downsizer</option>
                                        <option value="spouse">Spouse contribution</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-emerald-900 mb-1">When</label>
                                    <select
                                        value={row.scheduleType || 'year'}
                                        onChange={(e) => updateLumpSumContributionRow(idx, { scheduleType: e.target.value })}
                                        className="w-full px-3 py-3 text-base border-2 border-emerald-200 rounded-lg bg-white"
                                        style={{ fontSize: '16px' }}
                                    >
                                        <option value="age">Age</option>
                                        <option value="year">Plan year</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-emerald-900 mb-1">
                                        {row.scheduleType === 'year' ? 'Year #' : 'Age'}
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={row.scheduleValue ?? ''}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const scheduleValue = Number(value);
                                            const minCurrentAge = Math.min(Number(inputs.yourAge) || Infinity, Number(inputs.partnerAge ?? inputs.yourAge) || Infinity);
                                            const shouldTreatAsPlanYear = (row.scheduleType || 'year') === 'age'
                                                && Number.isFinite(scheduleValue)
                                                && scheduleValue > 0
                                                && scheduleValue <= Number(inputs.periodLength || 0)
                                                && scheduleValue < minCurrentAge;
                                            updateLumpSumContributionRow(idx, {
                                                scheduleValue: value,
                                                scheduleType: shouldTreatAsPlanYear ? 'year' : (row.scheduleType || 'year'),
                                            });
                                        }}
                                        placeholder={(row.scheduleType || 'year') === 'year' ? '4' : String(inputs.yourAge || 64)}
                                        className="w-full px-4 py-3 text-base border-2 border-emerald-200 rounded-lg"
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-emerald-900 mb-1">Amount ($)</label>
                                    <input
                                        type="number"
                                        min="0"
                                        step="1000"
                                        value={row.amount ?? ''}
                                        onChange={(e) => updateLumpSumContributionRow(idx, { amount: e.target.value })}
                                        placeholder="50000"
                                        className="w-full px-4 py-3 text-base border-2 border-emerald-200 rounded-lg"
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-emerald-900 mb-1">Label (optional)</label>
                                    <input
                                        type="text"
                                        value={row.label ?? ''}
                                        onChange={(e) => updateLumpSumContributionRow(idx, { label: e.target.value })}
                                        placeholder="Inheritance, downsizer"
                                        className="w-full px-4 py-3 text-base border-2 border-emerald-200 rounded-lg"
                                        style={{ fontSize: '16px' }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                );
            },
        },
        {
            id: 10,
            title: 'TBC Settings',
            icon: '🔒',
            subtitle: `Cap: $${(inputs.generalTransferBalanceCap || FinancialConfig.TRANSFER_BALANCE_CAP).toLocaleString()}`,
            fields: [
                {
                    key: 'generalTransferBalanceCap',
                    label: 'General Transfer Balance Cap ($)',
                    type: 'currency',
                    min: 0,
                    helper: 'Current TBC limit (indexed annually)'
                }
            ]
        },
        {
            id: 11,
            title: 'Fees',
            icon: '💳',
            subtitle: `$${parseFloat(inputs.adminFees || 0)}/yr + ${(parseFloat(inputs.adminFeePercentage || 0) + parseFloat(inputs.investmentFees || 0)).toFixed(2)}%`,
            fields: [
                {
                    key: 'adminFees',
                    label: 'Fixed Admin Fee ($/year)',
                    type: 'currency',
                    min: 0
                },
                {
                    key: 'adminFeePercentage',
                    label: 'Admin Fee (%)',
                    type: 'percentage',
                    min: 0,
                    max: 5,
                    step: 0.01
                },
                {
                    key: 'investmentFees',
                    label: 'Investment Fee (%)',
                    type: 'percentage',
                    min: 0,
                    max: 5,
                    step: 0.01
                },
                {
                    key: 'investmentFeeCap',
                    label: 'Investment Fee Cap ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Maximum dollar amount for investment fees'
                },
                {
                    key: 'transactionCosts',
                    label: 'Transaction Costs (%)',
                    type: 'percentage',
                    min: 0,
                    max: 1,
                    step: 0.01,
                    helper: 'Trading costs and spreads'
                },
                {
                    key: 'insuranceFees',
                    label: 'Insurance Premium ($/year)',
                    type: 'currency',
                    min: 0
                }
            ]
        }
    ];
    
    const renderField = (field) => {
        if (field.showIf && !field.showIf()) return null;
        
        const value = inputs[field.key];
        
        return (
            <div key={field.key} className="mb-5">
                <label className="block text-base font-bold text-gray-900 mb-2">
                    {field.label}
                </label>
                
                {field.type === 'select' && (
                    <select
                        value={value}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        className="w-full px-4 py-4 text-base font-medium border-2 border-gray-300 rounded-xl bg-white focus:border-purple-500 focus:ring-4 focus:ring-purple-200"
                        style={{ fontSize: '16px' }}
                    >
                        {field.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                )}
                
                {field.type === 'checkbox' && (
                    <label className={`flex items-center gap-4 p-5 bg-gray-50 rounded-xl border-2 border-gray-200 active:bg-gray-100 ${field.disabled ? 'opacity-60 cursor-not-allowed' : ''}`}>
                        <input
                            type="checkbox"
                            checked={value || false}
                            disabled={field.disabled}
                            onChange={(e) => handleInputChange(field.key, e.target.checked)}
                            className="w-7 h-7 text-purple-600 rounded-md flex-shrink-0"
                        />
                        <span className="text-base font-medium text-gray-800 flex-1">
                            {field.helper || field.label}
                        </span>
                    </label>
                )}
                
                {(field.type === 'number' || field.type === 'currency' || field.type === 'percentage') && (
                    <div className="relative">
                        <input
                            type="number"
                            value={field.disabled ? 0 : (value === '' ? '' : value)}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '') {
                                    handleInputChange(field.key, '');
                                } else {
                                    handleInputChange(field.key, parseFloat(val) || 0);
                                }
                            }}
                            placeholder={field.type === 'currency' ? '0' : field.type === 'percentage' ? '0.0' : '0'}
                            min={field.min}
                            max={field.max}
                            step={field.step || 1}
                            disabled={field.disabled}
                            className={`w-full px-5 py-5 text-xl font-bold text-right border-2 rounded-xl focus:ring-4 ${
                                field.disabled 
                                    ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed' 
                                    : 'border-gray-300 focus:border-purple-500 focus:ring-purple-200'
                            }`}
                            style={{ fontSize: '18px' }}
                        />
                        {field.type === 'currency' && (
                            <span className={`absolute left-5 top-1/2 -translate-y-1/2 text-xl font-bold ${field.disabled ? 'text-gray-400' : 'text-gray-500'}`}>$</span>
                        )}
                        {field.type === 'percentage' && (
                            <span className={`absolute right-5 top-1/2 -translate-y-1/2 text-xl font-bold ${field.disabled ? 'text-gray-400' : 'text-gray-500'}`}>%</span>
                        )}
                    </div>
                )}
                
                {field.warning && (
                    <p className="mt-2 text-sm text-orange-600 leading-relaxed">{field.warning}</p>
                )}
                
                {field.helper && field.type !== 'checkbox' && !field.warning && (
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{field.helper}</p>
                )}
            </div>
        );
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingBottom: hasCalculated ? 'max(calc(env(safe-area-inset-bottom) + 1.5rem), 2rem)' : 'max(calc(env(safe-area-inset-bottom) + 7.5rem), 8rem)' }}>
            {/* Loading Spinner - Calculations */}
            {isCalculating && (
                <MobileLoadingSpinner 
                    message={calculationMessage}
                    subMessage="This may take a few seconds..."
                />
            )}
            
            {/* Loading Spinner - PDF Export */}
            {isExporting && (
                <MobileLoadingSpinner 
                    message="Generating PDF..."
                    subMessage="Please wait while we create your report..."
                />
            )}
            
            {/* Header */}
            <div className={`bg-purple-600 text-white px-3 sticky top-0 z-20 shadow-md transition-all ${
                isScrolled ? 'py-2' : 'py-3'
            }`}
                 style={{ paddingTop: isScrolled ? '8px' : 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button onClick={onBack} className="p-1 active:bg-purple-700 rounded">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className={`font-bold transition-all ${isScrolled ? 'text-sm' : 'text-base'}`}>
                        Advanced
                    </h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            {!hasAdvancedAccess && freeRunsStatus.canRun && (
                <div className={`px-3 py-2 text-center text-xs font-semibold ${
                    freeRunsStatus.runsLeft === 1 ? 'bg-amber-100 text-amber-900' : 'bg-purple-50 text-purple-800'
                }`}>
                    {freeRunsStatus.runsLeft === 1
                        ? 'Last free Advanced run remaining'
                        : `${freeRunsStatus.runsLeft} free Advanced run${freeRunsStatus.runsLeft === 1 ? '' : 's'} remaining`}
                </div>
            )}

            {showFreeRunsEmailGate && !hasAdvancedAccess && (
                <div className="px-3 pt-3">
                    <FreeRunsEmailGate
                        onEmailCaptured={() => {
                            setFreeRunsStatus(SubscriptionManager.canRunAdvancedFree());
                            setShowFreeRunsEmailGate(false);
                        }}
                        onUpgradeClick={() => {
                            setShowFreeRunsEmailGate(false);
                            openPricingModal({
                                source: 'free_runs_email_gate',
                                calculatorTier: 'advanced',
                                surface: 'advanced_mobile_email_gate_upgrade',
                            });
                        }}
                        onDismiss={() => setShowFreeRunsEmailGate(false)}
                    />
                </div>
            )}

            <div className="px-3 pt-3 pb-1">
                <p className="text-base font-semibold text-slate-900">
                    Enter your numbers. See if the income survives bad markets.
                </p>
            </div>
            
            {/* Accordion Sections */}
            <div className="p-3 space-y-2">
                {sections.map((section, index) => (
                    <div key={section.id} id={`advanced-section-${index}`} className="bg-white rounded-xl shadow-sm overflow-hidden">
                        {/* Section Header */}
                        <button
                            onClick={() => toggleSection(index)}
                            className="w-full p-4 flex items-center justify-between active:bg-gray-50 transition"
                        >
                            <div className="flex items-center gap-3 flex-1">
                                <span className="text-2xl">{section.icon}</span>
                                <div className="text-left">
                                    <div className="font-bold text-gray-900">{section.title}</div>
                                    <div className="text-xs text-gray-600">{section.subtitle}</div>
                                </div>
                            </div>
                            <svg
                                className={`w-5 h-5 text-gray-400 transition-transform ${
                                    expandedSection === index ? 'rotate-180' : ''
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                        
                        {/* Section Content */}
                        {expandedSection === index && (
                            <div className="p-4 border-t border-gray-100">
                                <div className="space-y-4">
                                    {section.fields.map(field => renderField(field))}
                                    {section.customContent?.()}
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>
            
            {/* Results */}
            {hasCalculated && results && (
                <div ref={resultsRef} className="p-4 space-y-4 mb-20">
                    {/* Tabs: Historical vs Monte Carlo */}
                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setActiveTab('historical')}
                            className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition ${
                                activeTab === 'historical' 
                                    ? 'bg-blue-600 text-white shadow-lg' 
                                    : 'bg-gray-200 text-gray-700 active:bg-gray-300'
                            }`}
                        >
                            📊 Historical Analysis
                        </button>
                        <button
                            onClick={() => setActiveTab('monteCarlo')}
                            className={`flex-1 py-3 px-4 rounded-xl font-bold text-sm transition ${
                                activeTab === 'monteCarlo' 
                                    ? 'bg-green-600 text-white shadow-lg' 
                                    : 'bg-gray-200 text-gray-700 active:bg-gray-300'
                            }`}
                        >
                            🎲 Monte Carlo
                        </button>
                    </div>
                    
                    {activeTab === 'historical' && (
                    <>
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-1 rounded-2xl shadow-xl">
                        <div className="bg-white rounded-xl p-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                                Your Results
                            </h2>

                            {inputs.enableBridgeStrategy && (() => {
                                const isFixedRun = String(inputs.withdrawalStrategy || 'fixed').toLowerCase() === 'fixed';
                                const rows = results.chartData || [];
                                const bridgeDrawn = rows.some((row) => (row.bridgeWithdrawal || 0) > 0);
                                const bridgeWarning = rows.some((row) => row.bridgeDepletedBeforeAccess || (row.bridgeShortfall || 0) > 0);
                                return (
                                    <div className={`p-4 rounded-xl border-2 mb-4 text-sm ${
                                        isFixedRun
                                            ? 'bg-amber-50 border-amber-200 text-amber-900'
                                            : 'bg-orange-50 border-orange-200 text-orange-900'
                                    }`}>
                                        <strong>Bridge / delayed super access:</strong>{' '}
                                        {isFixedRun
                                            ? `Applied in this Fixed Sustainable Income run${bridgeDrawn ? '; bridge draws are included in the yearly results' : '; no bridge draw was needed in the displayed years'}.`
                                            : 'Not applied in this run because bridge buckets are currently modelled only for Fixed Sustainable Income.'}
                                        {bridgeWarning && (
                                            <span className="block mt-1 font-semibold">
                                                Bridge assets were depleted or short before super access in at least one projection year.
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}

                            {inputs.enableMortgageComparison && (() => {
                                const isFixedRun = String(inputs.withdrawalStrategy || 'fixed').toLowerCase() === 'fixed';
                                const rows = results.chartData || [];
                                const mortgageRows = rows.filter((row) => (row.mortgageOpeningBalance || 0) > 0 || (row.mortgageOutflow || 0) > 0);
                                const lastMortgageRow = mortgageRows[mortgageRows.length - 1] || {};
                                const totalInterest = mortgageRows.reduce((sum, row) => sum + (row.mortgageInterest || 0), 0);
                                return (
                                    <div className={`p-4 rounded-xl border-2 mb-4 text-sm ${
                                        isFixedRun
                                            ? 'bg-slate-50 border-slate-200 text-slate-900'
                                            : 'bg-orange-50 border-orange-200 text-orange-900'
                                    }`}>
                                        <strong>Mortgage payoff strategy comparison:</strong>{' '}
                                        {isFixedRun
                                            ? `Applied in this Fixed Sustainable Income run. Final mortgage balance: $${Math.round(lastMortgageRow.mortgageBalance || 0).toLocaleString()}; total interest: $${Math.round(totalInterest).toLocaleString()}.`
                                            : 'Not applied in this run because mortgage comparison is currently modelled only for Fixed Sustainable Income.'}
                                    </div>
                                );
                            })()}
                            
                            {/* Final Balance */}
                            <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200 mb-4">
                                <div className="text-sm font-semibold text-gray-600 mb-2">
                                    Final Super Balance
                                </div>
                                <div className="text-4xl font-bold text-blue-600 mb-1">
                                    ${results.finalBalance.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                    After {inputs.periodLength} years
                                </div>
                            </div>
                            
                            {/* Max Sustainable Income */}
                            <div className="bg-purple-50 p-6 rounded-xl border-2 border-purple-200 mb-4">
                                <div className="text-sm font-semibold text-gray-600 mb-2">
                                    Maximum Annual Income
                                </div>
                                <div className="text-4xl font-bold text-purple-600 mb-1">
                                    ${results.maxSustainableIncome.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                    Per year for {inputs.periodLength} years
                                </div>
                            </div>
                            
                            {/* Survival Probability */}
                            <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200 mb-4">
                                <div className="text-sm font-semibold text-gray-600 mb-2">
                                    Survival Probability
                                </div>
                                <div className="text-4xl font-bold text-green-600 mb-1">
                                    {isMonteCarloRunning
                                      ? 'Calculating…'
                                      : results.successRate != null
                                        ? `${results.successRate}%`
                                        : '—'}
                                </div>
                                <div className="text-xs text-gray-500">
                                    {isMonteCarloRunning
                                      ? 'Monte Carlo running after historical results'
                                      : 'Based on 1,000 Monte Carlo trials'}
                                </div>
                            </div>

                            {/* Historical periods at/above target */}
                            {(() => {
                                const usedTargetIncome = Number(inputs.monteCarloTargetIncome) > 0;
                                const msiHitThreshold = usedTargetIncome
                                    ? Number(inputs.monteCarloTargetIncome)
                                    : Number(results.maxSustainableIncome) || 0;
                                const rollingFinite = (rollingMSI || []).filter((row) => Number.isFinite(Number(row.income)));
                                const msiHitCount = rollingFinite.filter((row) => Number(row.income) >= msiHitThreshold).length;
                                const msiHitRatePct = rollingFinite.length
                                    ? Math.round((msiHitCount / rollingFinite.length) * 100)
                                    : null;
                                const fmt = (v) => `$${Math.round(Number(v || 0)).toLocaleString()}`;
                                return (
                                    <div className="bg-indigo-50 p-6 rounded-xl border-2 border-indigo-200">
                                        <div className="text-sm font-semibold text-gray-600 mb-2">
                                            Historical periods at/above target
                                        </div>
                                        <div className="text-4xl font-bold text-indigo-600 mb-1">
                                            {msiHitRatePct != null ? `${msiHitRatePct}%` : 'n/a'}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {msiHitRatePct != null
                                                ? `${msiHitCount} of ${rollingFinite.length} rolling periods · ${usedTargetIncome ? `Target ${fmt(msiHitThreshold)}` : `Safe income ${fmt(msiHitThreshold)}`}`
                                                : 'Run historical analysis to compare periods'}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    {/* Rolling Period Analysis - Historical Outcomes */}
                    {rollingMSI && rollingMSI.length > 0 && (() => {
                        const incomes = rollingMSI.map(d => d.income).filter(n => Number.isFinite(n)).sort((a, b) => a - b);
                        if (incomes.length === 0) return null;
                        
                        const lowest = incomes[0];
                        const highest = incomes[incomes.length - 1];
                        const mid = Math.floor(incomes.length / 2);
                        const average = incomes.length % 2 === 1 ? incomes[mid] : Math.round((incomes[mid - 1] + incomes[mid]) / 2);
                        const currentPeriodResult = rollingMSI.find(d => d.start === parseInt(inputs.startYear));
                        const currentPeriod = currentPeriodResult ? currentPeriodResult.income : results.maxSustainableIncome;
                        
                        return (
                            <div className="mt-4">
                                <h3 className="text-lg font-bold text-gray-900 mb-3 px-2">
                                    Historical Analysis ({rollingMSI.length} periods tested)
                                </h3>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                        <div className="text-xs font-semibold text-green-700 mb-1">Lowest Income</div>
                                        <div className="text-lg font-bold text-green-900">${lowest.toLocaleString()}</div>
                                    </div>
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                        <div className="text-xs font-semibold text-green-700 mb-1">Average Income</div>
                                        <div className="text-lg font-bold text-green-900">${average.toLocaleString()}</div>
                                    </div>
                                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                                        <div className="text-xs font-semibold text-green-700 mb-1">Highest Income</div>
                                        <div className="text-lg font-bold text-green-900">${highest.toLocaleString()}</div>
                                    </div>
                                    <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                                        <div className="text-xs font-semibold text-purple-700 mb-1">Your Period ({inputs.startYear})</div>
                                        <div className="text-lg font-bold text-purple-900">${currentPeriod.toLocaleString()}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* Email Capture - After Results */}
                    <EmailCaptureForm 
                        calculatorType="advanced"
                        results={results}
                        inputs={inputs}
                        getPdfBase64={async () => PDFExportManager.exportAdvancedCalculator(
                            inputs,
                            {
                                sustainableIncome: results.maxSustainableIncome,
                                finalRealBalance: results.finalBalance,
                                yearlyData: results.chartData,
                                rollingPeriodStats: rollingMSI.length > 0 ? {
                                    count: rollingMSI.length,
                                    lowest: Math.min(...rollingMSI.map(d => d.income)),
                                    highest: Math.max(...rollingMSI.map(d => d.income)),
                                    average: rollingMSI.reduce((sum, d) => sum + d.income, 0) / rollingMSI.length
                                } : null
                            },
                            {
                                successRate: results.successRate || 100,
                                medianFinalBalance: results.finalBalance,
                                percentile10: results.finalBalance * 0.8,
                                percentile90: results.finalBalance * 1.2,
                                simulationCount: 1000
                            },
                            inputs.withdrawalStrategy || 'fixed',
                            {},
                            true
                        )}
                    />
                    
                    {/* Action Buttons */}
                    <div className="space-y-3">
                        <button 
                            onClick={() => setShowDetailedChart(true)}
                            className="w-full py-4 bg-purple-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-purple-700"
                        >
                            📊 View Detailed Chart
                        </button>
                        <button 
                            onClick={async () => {
                                if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                                    startPdfUnlockCheckout({
                                        calculatorTier: 'advanced',
                                        surface: 'advanced_mobile_pdf_historical',
                                    }).catch(alertCheckoutFailure);
                                    return;
                                }
                                try {
                                    setIsExporting(true);
                                    await new Promise(resolve => {
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                setTimeout(resolve, 100);
                                            });
                                        });
                                    });
                                    
                                    // Use EXACT same function as desktop
                                    await PDFExportManager.exportAdvancedCalculator(
                                        inputs,
                                        {
                                            sustainableIncome: results.maxSustainableIncome,
                                            finalRealBalance: results.finalBalance,
                                            yearlyData: results.chartData,
                                            rollingPeriodStats: rollingMSI.length > 0 ? {
                                                count: rollingMSI.length,
                                                lowest: Math.min(...rollingMSI.map(d => d.income)),
                                                highest: Math.max(...rollingMSI.map(d => d.income)),
                                                average: rollingMSI.reduce((sum, d) => sum + d.income, 0) / rollingMSI.length
                                            } : null
                                        },
                                        {
                                            successRate: results.successRate || 100,
                                            medianFinalBalance: results.finalBalance,
                                            percentile10: results.finalBalance * 0.8,
                                            percentile90: results.finalBalance * 1.2,
                                            simulationCount: 1000
                                        },
                                        inputs.withdrawalStrategy || 'fixed',
                                        {}  // Chart images
                                    );
                                    setIsExporting(false);
                                } catch (error) {
                                    console.error('PDF export error:', error);
                                    setIsExporting(false);
                                    alert('Error generating PDF. Please try again.');
                                }
                            }}
                            className="w-full py-4 bg-gray-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-gray-700"
                        >
                            📄 Export PDF
                        </button>
                        <button 
                            onClick={() => {
                                setHasCalculated(false);
                                setResults(null);
                                setExpandedSection(0);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="w-full py-4 bg-white text-gray-700 font-bold text-base rounded-xl border-2 border-gray-300"
                        >
                            🔄 New Calculation
                        </button>
                    </div>

                    <CalculationTransparency
                        kind="advanced"
                        inputs={inputs}
                        results={{ ...results, incomeByYear: results.chartData || [] }}
                        chartData={results.chartData || []}
                        className="mt-6"
                    />
                    </>
                    )}

                    {/* Monte Carlo Tab */}
                    {activeTab === 'monteCarlo' && monteCarloResults && (
                        <div className="space-y-4">
                            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-1 rounded-2xl shadow-xl">
                                <div className="bg-white rounded-xl p-6">
                                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Monte Carlo Simulation</h2>
                                    <p className="text-sm text-gray-600 mb-6">1,000 random market scenarios tested</p>
                                    
                                    {/* Median Income Summary - SHOWN FIRST (Desktop parity) */}
                                    {monteCarloResults.medianIncomesSummary && (
                                        <div className="mb-4 bg-green-50 p-4 rounded-xl border-2 border-green-200">
                                            <h4 className="text-sm font-semibold text-green-900 mb-1">Expected Real Income Range</h4>
                                            <p className="text-xs text-green-700 mb-3">This shows the range of median incomes from across all 1000 simulations.</p>
                                            <div className="grid grid-cols-3 gap-2">
                                                <div className="bg-white/70 border border-green-200 rounded p-3 text-center">
                                                    <div className="text-[10px] uppercase tracking-wide text-green-700 mb-1">Worst Case Median</div>
                                                    <div className="text-base font-bold text-green-900">${monteCarloResults.medianIncomesSummary.worst.toLocaleString()}</div>
                                                    <div className="text-[9px] text-green-600 mt-1">Lowest median income</div>
                                                </div>
                                                <div className="bg-white/70 border border-green-200 rounded p-3 text-center">
                                                    <div className="text-[10px] uppercase tracking-wide text-green-700 mb-1">Median of Medians</div>
                                                    <div className="text-base font-bold text-green-900">${monteCarloResults.medianIncomesSummary.median.toLocaleString()}</div>
                                                </div>
                                                <div className="bg-white/70 border border-green-200 rounded p-3 text-center">
                                                    <div className="text-[10px] uppercase tracking-wide text-green-700 mb-1">Best Case Median</div>
                                                    <div className="text-base font-bold text-green-900">${monteCarloResults.medianIncomesSummary.best.toLocaleString()}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Survival Probability */}
                                    <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200 mb-4">
                                        <div className="text-sm font-semibold text-blue-900 mb-2">Survival Probability</div>
                                        <div className="text-4xl font-bold text-blue-600 mb-1">{monteCarloResults.successRate}%</div>
                                        <div className="text-xs text-blue-700 mb-3">
                                            Based on 1,000 Monte Carlo trials over {inputs.periodLength} years
                                        </div>
                                        <div className="bg-blue-100 border border-blue-200 rounded p-2 mb-3">
                                            <div className="text-xs font-semibold text-blue-900">
                                                {inputs.monteCarloTargetIncome && Number(inputs.monteCarloTargetIncome) > 0
                                                    ? `Tested with a fixed income of $${Number(inputs.monteCarloTargetIncome).toLocaleString()}`
                                                    : 'Using calculated sustainable income'}
                                            </div>
                                        </div>
                                        
                                        {/* Outcome Percentiles - Inside success rate box (Desktop parity) */}
                                        <div className="bg-white/60 border border-blue-100 rounded p-3">
                                            <div className="text-xs font-semibold text-blue-900 mb-2">Outcome percentiles</div>
                                            <div className="space-y-1">
                                                <div className="flex justify-between items-center text-xs">
                                                    <span className="text-blue-800 font-medium">Percentile</span>
                                                    <span className="text-blue-800 font-medium">Income</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs">
                                                    <span className="text-blue-900">10th</span>
                                                    <span className="font-semibold text-blue-900">${monteCarloResults.incomePercentiles.p10.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs">
                                                    <span className="text-blue-900">25th</span>
                                                    <span className="font-semibold text-blue-900">${monteCarloResults.incomePercentiles.p25.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs bg-blue-100 -mx-1 px-1 py-0.5 rounded">
                                                    <span className="text-blue-900 font-semibold">50th</span>
                                                    <span className="font-bold text-blue-900">${monteCarloResults.incomePercentiles.p50.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs">
                                                    <span className="text-blue-900">75th</span>
                                                    <span className="font-semibold text-blue-900">${monteCarloResults.incomePercentiles.p75.toLocaleString()}</span>
                                                </div>
                                                <div className="flex justify-between items-center text-xs">
                                                    <span className="text-blue-900">90th</span>
                                                    <span className="font-semibold text-blue-900">${monteCarloResults.incomePercentiles.p90.toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Historical periods at/above target */}
                                    {(() => {
                                        const usedTargetIncome = Number(inputs.monteCarloTargetIncome) > 0;
                                        const msiHitThreshold = usedTargetIncome
                                            ? Number(inputs.monteCarloTargetIncome)
                                            : Number(results?.maxSustainableIncome) || 0;
                                        const rollingFinite = (rollingMSI || []).filter((row) => Number.isFinite(Number(row.income)));
                                        const msiHitCount = rollingFinite.filter((row) => Number(row.income) >= msiHitThreshold).length;
                                        const msiHitRatePct = rollingFinite.length
                                            ? Math.round((msiHitCount / rollingFinite.length) * 100)
                                            : null;
                                        const fmt = (v) => `$${Math.round(Number(v || 0)).toLocaleString()}`;
                                        return (
                                            <div className="bg-indigo-50 p-6 rounded-xl border-2 border-indigo-200 mb-4">
                                                <div className="text-sm font-semibold text-indigo-900 mb-2">Historical periods at/above target</div>
                                                <div className="text-4xl font-bold text-indigo-600 mb-1">
                                                    {msiHitRatePct != null ? `${msiHitRatePct}%` : 'n/a'}
                                                </div>
                                                <div className="text-xs text-indigo-700">
                                                    {msiHitRatePct != null
                                                        ? `${msiHitCount} of ${rollingFinite.length} rolling periods · ${usedTargetIncome ? `Target ${fmt(msiHitThreshold)}` : `Safe income ${fmt(msiHitThreshold)}`}`
                                                        : 'Run historical analysis to compare periods'}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    
                                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200 text-sm text-blue-800">
                                    <p className="text-xs text-gray-600 mt-2">
                                        <strong>Note:</strong> Monte Carlo uses the same strategy engines as Historical Analysis (1000 random historical-year paths).
                                    </p> 
                                        Results show the survival probability and range of potential outcomes.
                                    </div>
                                </div>
                            </div>
                            
                            {/* Export button for Monte Carlo */}
                            <button 
                                onClick={async () => {
                                    if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                                        startPdfUnlockCheckout({
                                            calculatorTier: 'advanced',
                                            surface: 'advanced_mobile_pdf_montecarlo',
                                        }).catch(alertCheckoutFailure);
                                        return;
                                    }
                                    try {
                                        setIsExporting(true);
                                        await new Promise(resolve => {
                                            requestAnimationFrame(() => {
                                                requestAnimationFrame(() => {
                                                    setTimeout(resolve, 100);
                                                });
                                            });
                                        });
                                        
                                        await PDFExportManager.exportAdvancedCalculator(
                                            inputs,
                                            {
                                                sustainableIncome: results.maxSustainableIncome,
                                                finalRealBalance: results.finalBalance,
                                                yearlyData: results.chartData,
                                                rollingPeriodStats: rollingMSI.length > 0 ? {
                                                    count: rollingMSI.length,
                                                    lowest: Math.min(...rollingMSI.map(d => d.income)),
                                                    highest: Math.max(...rollingMSI.map(d => d.income)),
                                                    average: rollingMSI.reduce((sum, d) => sum + d.income, 0) / rollingMSI.length
                                                } : null
                                            },
                                            {
                                                successRate: results.successRate || 100,
                                                medianFinalBalance: results.finalBalance,
                                                percentile10: Math.round(results.finalBalance * 0.6),
                                                percentile90: Math.round(results.finalBalance * 1.4),
                                                simulationCount: 1000
                                            },
                                            inputs.withdrawalStrategy || 'fixed',
                                            {}
                                        );
                                        setIsExporting(false);
                                    } catch (error) {
                                        console.error('PDF export error:', error);
                                        setIsExporting(false);
                                        alert('Error generating PDF. Please try again.');
                                    }
                                }}
                                className="w-full py-4 bg-gray-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-gray-700"
                            >
                                📄 Export PDF
                            </button>
                        </div>
                    )}
                </div>
            )}
            
            {/* Detailed Chart Modal */}
            {showDetailedChart && results && (
                <div className="fixed inset-0 bg-black bg-opacity-70 z-50 overflow-auto" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
                    <div className="min-h-screen p-4">
                        <div className="bg-white rounded-xl w-full max-w-4xl mx-auto mb-4">
                            {/* Modal Header */}
                            <div className="bg-purple-600 text-white p-4 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
                                <h3 className="text-lg font-bold">Advanced Analysis</h3>
                                <button 
                                    onClick={() => setShowDetailedChart(false)}
                                    className="text-white bg-purple-700 hover:bg-purple-800 p-2 rounded-lg w-10 h-10 flex items-center justify-center"
                                >
                                    ✕
                                </button>
                            </div>
                            
                            {/* Modal Content - Scrollable */}
                            <div className="p-4 space-y-6">
                                {/* Balance Chart - DUAL TRAJECTORIES */}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-3">Super Balance Over Time</h3>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <AreaChart data={results.chartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                                            <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                                            <Tooltip formatter={(val) => `$${val.toLocaleString()}`} />
                                            <Legend />
                                            <Area 
                                                type="monotone" 
                                                dataKey="endingBalance" 
                                                name="Max Sustainable"
                                                stroke="#9333EA" 
                                                fill="#9333EA" 
                                                fillOpacity={0.3}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                                
                                {/* Income Chart - Match Desktop Colors Exactly */}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-3">Real Annual Income by Source</h3>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <ComposedChart data={results.chartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                                            <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                                            <Tooltip formatter={(val) => `$${val.toLocaleString()}`} />
                                            <Legend />
                                            <Bar dataKey="otherTaxableIncome" stackId="income" fill="#a78bfa" name="Other Taxable Income" />
                                            <Bar dataKey="otherNonTaxableIncome" stackId="income" fill="#8B5CF6" name="Other Non-Taxable Income" />
                                            <Bar dataKey="withdrawalFromSuper" stackId="income" fill="#60a5fa" name="Withdrawal from Super" />
                                            <Bar dataKey="lumpSumWithdrawal" stackId="income" fill="#f59e0b" name="Lump Sum Withdrawal" />
                                            <Bar dataKey="pension" stackId="income" fill="#34d399" name="Pension" />
                                            {results.chartData?.some((row) => (row.lumpSumContribution || 0) > 0) && (
                                                <Line type="monotone" dataKey="lumpSumContribution" name="Super Contribution" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                                            )}
                                            {/* Show target income line if user specified one */}
                                            {inputs.monteCarloTargetIncome && Number(inputs.monteCarloTargetIncome) > 0 && (
                                                <Line 
                                                    type="monotone" 
                                                    dataKey={() => Number(inputs.monteCarloTargetIncome)}
                                                    stroke="#EF4444" 
                                                    strokeWidth={2}
                                                    dot={false}
                                                    name="Target Income"
                                                />
                                            )}
                                            {/* For Fixed strategy: show average total income across all years */}
                                            {(inputs.withdrawalStrategy === 'Fixed' || inputs.withdrawalStrategy === 'fixed') && (() => {
                                                const rowTotal = (row) => Number.isFinite(row.totalIncome)
                                                  ? row.totalIncome
                                                  : (row.otherTaxableIncome || 0) + (row.otherNonTaxableIncome || 0) + (row.annuityIncome || 0) + (row.definedBenefitPension || 0) + (row.bridgeWithdrawal || 0) + (row.withdrawalFromSuper || 0) + (row.lumpSumWithdrawal || 0) + (row.pension || 0) - (row.mortgageOutflow || 0);
                                                const allIncomes = (results.chartData || []).map(rowTotal);
                                                if (allIncomes.length === 0) return null;
                                                const avgIncomeAllYears = allIncomes.reduce((sum, inc) => sum + inc, 0) / allIncomes.length;
                                                return (
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey={() => avgIncomeAllYears}
                                                        stroke="#DC2626" 
                                                        strokeWidth={2}
                                                        strokeDasharray="5 5"
                                                        dot={false}
                                                        name={`Average income (${allIncomes.length} yrs)`}
                                                    />
                                                );
                                        })()}
                                        </ComposedChart>
                                    </ResponsiveContainer>
                                    {(inputs.withdrawalStrategy === 'Fixed' || inputs.withdrawalStrategy === 'fixed') && results.chartData?.length > 0 && (() => {
                                        const rowTotal = (row) => Number.isFinite(row.totalIncome)
                                          ? row.totalIncome
                                          : (row.otherTaxableIncome || 0) + (row.otherNonTaxableIncome || 0) + (row.annuityIncome || 0) + (row.definedBenefitPension || 0) + (row.bridgeWithdrawal || 0) + (row.withdrawalFromSuper || 0) + (row.lumpSumWithdrawal || 0) + (row.pension || 0) - (row.mortgageOutflow || 0);
                                        const allIncomes = results.chartData.map(rowTotal);
                                        const avgIncomeAllYears = allIncomes.reduce((sum, inc) => sum + inc, 0) / allIncomes.length;
                                        return (
                                            <p className="text-xs text-gray-600 mt-2 italic">
                                                <strong>Why isn't Fixed income perfectly flat?</strong> There can be up to three different income levels in phased retirement scenarios:
                                                Phase 1 (both partners under 60, no super access), Phase 2 (one partner 60+, one under 60), and Phase 3 (both partners 60+).
                                                Australian law also requires minimum annual withdrawals from super based on your age (4% under 65, 5% at 65–74, 6% at 75–79, then higher) once you're eligible to access it — that minimum is a floor under Fixed MSI, and it still applies while you're working under phased retirement.
                                                Two things push income above the steady-state MSI you see in later years: (1) in a strong market year, the mandatory minimum (a % of the retired partner's balance) can exceed MSI; or (2) under phased retirement, the retired partner's minimum is withdrawn on top of the still-working partner's salary — the working partner's super is not drawn and does not take a minimum until they retire.
                                                You must withdraw the higher amount to stay compliant, even if you don't need the income for lifestyle. The red dotted line shows the average total income across all {allIncomes.length} years: <strong>${Math.round(avgIncomeAllYears).toLocaleString()}/year</strong>.
                                                Age Pension changes can also cause variations.
                                            </p>
                                        );
                                    })()}
                                </div>
                                
                                {/* Rolling Period Chart */}
                                {rollingMSI && rollingMSI.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-3">Rolling Period Analysis</h3>
                                        <p className="text-xs text-gray-600 mb-3">
                                            This shows the outcome for every possible {inputs.periodLength}-year period in historical data
                                        </p>
                                        <ResponsiveContainer width="100%" height={250}>
                                            <BarChart data={rollingMSI}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="start" label={{ value: 'Start Year', position: 'insideBottom', offset: -5 }} />
                                                <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                                                <Tooltip formatter={(val) => `$${val.toLocaleString()}`} labelFormatter={(l) => `Start ${l}`} />
                                                <Bar dataKey="income" name="Max Sustainable Income" fill="#6366f1" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                                
                                {/* Data Table */}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-3">Year-by-Year Details</h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm border-collapse">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="p-2 text-left border">Year</th>
                                                    <th className="p-2 text-left border">Age</th>
                                                    <th className="p-2 text-right border">Balance</th>
                                                    <th className="p-2 text-right border">Income</th>
                                                    <th className="p-2 text-right border">Pension</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.chartData.map((row, idx) => (
                                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                        <td className="p-2 border">{row.year}</td>
                                                        <td className="p-2 border">{row.age}</td>
                                                        <td className="p-2 text-right border font-bold">${(row.endingBalance || 0).toLocaleString()}</td>
                                                        <td className="p-2 text-right border">${(row.totalIncome || 0).toLocaleString()}</td>
                                                        <td className="p-2 text-right border">${(row.pension || 0).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                                {/* What-If Scenarios - EXACT DESKTOP (3 scenarios) */}
                                {results && (
                                    <div>
                                        <div className="mb-3">
                                            <h3 className="text-lg font-bold text-gray-900 mb-1">What-If Scenarios</h3>
                                            <button
                                                onClick={() => setShowWhatIf(!showWhatIf)}
                                                className="text-sm font-semibold text-purple-600 active:text-purple-700"
                                            >
                                                {showWhatIf ? '▲ Hide Controls' : '▼ Show Controls'}
                                            </button>
                                        </div>
                                        
                                        {/* What-If Input Controls - EXACT DESKTOP (3 scenarios) */}
                                        {showWhatIf && (
                                            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Change Retirement Period (years):
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="number"
                                                            value={whatIfPeriod || ''}
                                                            onChange={(e) => {
                                                                const val = e.target.value.replace(/[^0-9]/g, '');
                                                                setWhatIfPeriod(Math.max(0, parseInt(val) || 0));
                                                            }}
                                                            placeholder="Years"
                                                            className="flex-1 px-4 py-3 text-base border-2 border-gray-300 rounded-lg"
                                                            style={{ fontSize: '16px' }}
                                                        />
                                                        <button
                                                            onClick={handleChangeRetirementPeriod}
                                                            className="px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg active:bg-blue-700"
                                                        >
                                                            Run
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Change Annual Fees (% points):
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="number"
                                                            step="0.1"
                                                            value={whatIfFeeChangePct}
                                                            onChange={(e) => setWhatIfFeeChangePct(e.target.value)}
                                                            placeholder="-0.5"
                                                            className="flex-1 px-4 py-3 text-base border-2 border-gray-300 rounded-lg"
                                                            style={{ fontSize: '16px' }}
                                                        />
                                                        <button
                                                            onClick={handleChangeAnnualFees}
                                                            className="px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg active:bg-blue-700"
                                                        >
                                                            Run
                                                        </button>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Sequence Risk (% point reduction for 3 years):
                                                    </label>
                                                    <div className="flex gap-2">
                                                        <input
                                                            type="number"
                                                            value={whatIfSeqRiskPct}
                                                            onChange={(e) => setWhatIfSeqRiskPct(e.target.value)}
                                                            placeholder="-20"
                                                            className="flex-1 px-4 py-3 text-base border-2 border-gray-300 rounded-lg"
                                                            style={{ fontSize: '16px' }}
                                                        />
                                                        <button
                                                            onClick={handleSequenceRisk}
                                                            className="px-4 py-3 bg-red-600 text-white font-semibold rounded-lg active:bg-red-700"
                                                        >
                                                            Run
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="space-y-3">
                                            {/* What-If Results - EXACT DESKTOP (3 scenarios) */}
                                            {whatIfPeriodResult && (
                                                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                                                    {whatIfPeriodResult.success === false ? (
                                                        <div className="text-sm text-red-600 font-medium">Error: {whatIfPeriodResult.error}</div>
                                                    ) : whatIfPeriodResult.success === true && whatIfPeriodResult.data ? (
                                                        <div>
                                                            <div className="text-sm font-bold text-blue-900 mb-1">📅 Retirement Period: {whatIfPeriod} years</div>
                                                            <div className="text-xs text-blue-700">
                                                                Sustainable income: ${(whatIfPeriodResult.data.avgIncome || 0).toLocaleString()}/year<br/>
                                                                Current ({inputs.periodLength} years): ${results.maxSustainableIncome.toLocaleString()}/year
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )}
                                            
                                            {whatIfFeeChangeResult && (
                                                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                                                    {whatIfFeeChangeResult.success === false ? (
                                                        <div className="text-sm text-red-600 font-medium">Error: {whatIfFeeChangeResult.error}</div>
                                                    ) : whatIfFeeChangeResult.success === true && whatIfFeeChangeResult.data ? (
                                                        <div>
                                                            <div className="text-sm font-bold text-blue-900 mb-1">💸 Fees {whatIfFeeChangeResult.data.feeChange >= 0 ? '+' : ''}{whatIfFeeChangeResult.data.feeChange}%</div>
                                                            <div className="text-xs text-blue-700">
                                                                Income: ${(whatIfFeeChangeResult.data.avgIncome || 0).toLocaleString()}/year<br/>
                                                                Impact: {((whatIfFeeChangeResult.data.avgIncome || 0) - results.maxSustainableIncome) >= 0 ? '+' : ''}${((whatIfFeeChangeResult.data.avgIncome || 0) - results.maxSustainableIncome).toLocaleString()}/year
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )}
                                            
                                            {whatIfSeqRiskResult && (
                                                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                                                    {whatIfSeqRiskResult.success === false ? (
                                                        <div className="text-sm text-red-600 font-medium">Error: {whatIfSeqRiskResult.error}</div>
                                                    ) : whatIfSeqRiskResult.success === true && whatIfSeqRiskResult.data ? (
                                                        <div>
                                                            <div className="text-sm font-bold text-red-900 mb-1">📉 Returns {whatIfSeqRiskResult.data.sequenceRiskPct >= 0 ? '+' : ''}{whatIfSeqRiskResult.data.sequenceRiskPct}% pts for 3 years</div>
                                                            <div className="text-xs text-red-700">
                                                                Balance after crash: ${(whatIfSeqRiskResult.data.balanceAfter3Years || 0).toLocaleString()}<br/>
                                                                Income: ${(whatIfSeqRiskResult.data.avgIncome || 0).toLocaleString()}/year<br/>
                                                                Impact: ${((whatIfSeqRiskResult.data.avgIncome || 0) - results.maxSustainableIncome).toLocaleString()}/year
                                                            </div>
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            
                            {/* Modal Footer */}
                            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
                                <button 
                                    onClick={() => setShowDetailedChart(false)}
                                    className="w-full py-3 bg-purple-600 text-white font-bold rounded-lg active:bg-purple-700"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Calculate Button */}
            {!hasCalculated && (
                <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-30 px-3 py-2"
                     style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 8px)' }}>
                    <button
                        onClick={handleCalculate}
                        className="w-full py-4 bg-purple-600 text-white font-bold text-base rounded-xl active:bg-purple-700 transition flex items-center justify-center gap-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        Calculate
                    </button>
                </div>
            )}
            <FixedIncomeVariesNote note={fixedIncomeVariesNote} onClose={() => setFixedIncomeVariesNote(null)} />
        </div>
    );
};

export default AdvancedCalculatorMobile;

